import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { auth, db, provider, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import { withTenant, _getCurrentTenantId } from './services/data';
import { subscribeOwnedTasks } from './lib/taskQueries';
import { isDelegatedByMe } from './lib/taskOwnership';
import { buildUserWithRole } from './lib/userWithRole';
import { postViewerFromUserDoc } from './lib/postAudience'; // RW-2957
import usePostScope from './hooks/usePostScope'; // RW-2957
import OnboardingFlow from './components/OnboardingFlow';
// RW-1024: new-hire intra-module navigation bar (My checklist · My info · Status — RW-1237 labels)
import { OnboardingBar } from './components/onboarding/OnboardingSubnav';
import { HIRE_NAV, activeHireId, canSeeStaffOnboardingNav } from './components/onboarding/onboardingNav';
import SearchBar from './components/SearchBar';
import MobileNav from './components/MobileNav';
import LegacyDeepLinkRedirect, { legacyDeepLinkTarget } from './components/LegacyDeepLinkRedirect';
import PushNotificationManager from './components/PushNotificationManager';
import PWAShell from './components/PWAShell';
import EventAlertBanner from './components/EventAlertBanner';
import ErrorBoundary from './components/ErrorBoundary';
import {
  AuthChecking, AuthSignIn, AuthRejected, AuthPopupFailed, AuthHandoff,
  AuthProvisioning, AuthSignedOut, AuthRouteLoader, firstNameOf,
} from './components/AuthScreens';
import NotificationBell from './components/NotificationBell';
import QuickActionsFab from './components/QuickActionsFab';
import BugReporter from './components/BugReporter';
import HeaderAccountMenu, { HEADER_CLUSTER_STYLE } from './components/HeaderAccountMenu';
import Sidebar, { useSidebarWidth } from './components/Sidebar';
// RW-68: header chrome glyphs — in-house Lucide SVGs (used ONLY inside render,
// never in module-level consts, per CLAUDE.md gotcha #11 / TDZ).
import { IconComment, IconAlert, IconSparkles } from './components/feedIcons';
import { initAnalytics, trackEvent } from './lib/analytics';
import { track, trackWithUser, EVENTS } from './analytics/track';
// RW-1789: one-shot marker — rail/search clicks tag the route change they cause.
import { markNavSource, consumeNavSource } from './analytics/navSource';
import { logPageView } from './utils/analyticsLogger';
import { isAgentItem } from './utils/onboardingPermissions';
import { useFeatureFlags } from './contexts/FeatureFlagsContext';
import { DealsRolesProvider } from './contexts/DealsRolesContext';
import { WhatsNewCalloutsProvider, useWhatsNewCalloutsState } from './contexts/WhatsNewCalloutsContext';
import { isFeatureEnabled, isPathEnabled, routeGuardDecision } from './lib/featureFlags';
import { fabStackReach } from './lib/fabStack'; // RW-3408
import { defaultLandingFor, TASK_INBOX_PATH } from './lib/roleLanding'; // RW-2867
import useTaskInboxRead from './hooks/useTaskInboxRead'; // RW-2867
import { localYmd, fromDealRow, fromOnboardingRow, fromOtherTask, inboxBadgeCount } from './lib/taskInbox'; // RW-2867
// RW-1786: topbar breadcrumb derives from the one nav tree.
import { navTrailForPath, connectDetailTrail } from './lib/navigation';
import { countAttentionPosts } from './lib/feedScheduled'; // RW-2986
// RW-859: shared with AdminDashboard's lazy sections
import lazyWithRetry from './lib/lazyWithRetry';
import { CelebrationProvider } from './components/CelebrationEngine';
import { ToastProvider } from './contexts/ToastContext';
import { DealCloseHoldProvider, flushHeldDealCloses } from './contexts/DealCloseHoldContext'; // RW-2440
import styles from './components/styles';
// theme/formatPostTime removed — unused in App.js
import useOnlineStatus from './hooks/useOnlineStatus';
import { useSharedUsers } from './contexts/UsersContext';
import { useViewMode } from './contexts/ViewModeContext';
import { useImpersonation } from './contexts/ImpersonationContext';
import { useTenant } from './contexts/TenantContext';
import { deriveRoleFlags, PERMANENT_SUPER_ADMIN } from './hooks/useUserRole';
import { isValidRole, CANONICAL_ROLES } from './lib/roles';
import { FocusProvider } from './contexts/FocusContext';
// RW-899 stage 1: shared allowlist for winding-down agents (also used by Sidebar)
import { isWindDownAllowedPath } from './lib/windDown';
// RW-1194/1192: concierge resolution + DM deep-link helpers.
import { resolveConciergeUser } from './components/onboarding/obKit';
import { convoId } from './components/chat/chatUtils';
// RW-2065: ONE definition of live / finished / archived (twinned server-side).
import { isArchivedInstance, isCompletedInstance } from './lib/onboardingInstance';

/**
 * RW-1194/1192: open (or create) a DM with the onboarding concierge (the live
 * agent_services user) and deep-link the Messages drawer to it. ChatDrawer's
 * ?open=<convoId> auto-open only opens an EXISTING conversation doc, so for a
 * first-time DM we ensure the doc exists first (mirrors ChatDrawer's create
 * shape). Falls back to the bare /messages inbox if the concierge can't be
 * resolved. Module-level (no hooks) to stay TDZ-safe in this file.
 */
async function openConciergeDM({ me, allUsers, navigate }) {
  const concierge = resolveConciergeUser(allUsers);
  if (!me?.uid || !concierge?.uid || concierge.uid === me.uid) { navigate('/messages'); return; }
  const cid = convoId(me.uid, concierge.uid);
  try {
    const ref = doc(db, 'conversations', cid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, withTenant('conversations', {
        type: 'direct',
        participants: [me.uid, concierge.uid].sort(),
        participantNames: { [me.uid]: me.displayName || 'Member', [concierge.uid]: concierge.displayName || 'Member' },
        participantPhotos: { [me.uid]: me.photoURL || '', [concierge.uid]: concierge.photoURL || '' },
        participantDetails: { [me.uid]: { name: me.displayName, avatar: me.photoURL || '' }, [concierge.uid]: { name: concierge.displayName, avatar: concierge.photoURL || '' } },
        lastMessage: '', lastMessageAt: serverTimestamp(), lastMessageBy: '',
        createdAt: serverTimestamp(), unreadCount: { [me.uid]: 0, [concierge.uid]: 0 },
      }));
    }
    navigate('/messages?open=' + cid);
  } catch (_) { navigate('/messages'); }
}

/* ─── Route ↔ Nav mapping ─── */
const NAV_TO_PATH = {
  Dashboard: '/dashboard',
  Home: '/home', Feed: '/feed', People: '/people', 'Org Chart': '/org-chart', Offices: '/offices', Groups: '/groups', Vendors: '/vendors',
  StaffHR: '/staff', // Staff · HR — separate admin-only workspace (RW-1046/1047)
  Calendar: '/calendar',
  'My Tasks': '/my-tasks',
  Photos: '/photos',
  Events: '/events', Leaderboard: '/leaderboard', Documents: '/documents', Training: '/training', Tutorials: '/tutorials',
  Recognition: '/recognition',
  'My Production': '/my-production',
  Reports: '/reports',
  'My Numbers': '/my-numbers', // RW-2013
  'Rêve Sign': '/sign', // RW-494 signature requests (DocuSeal since RW-3307)
  Listings: '/listings',
  Flipbooks: '/flipbooks',
  Buyers: '/buyers',
  Contacts: '/contacts',
  'Saved Posts': '/saved',
  Onboarding: '/onboarding', Admin: '/admin', Settings: '/settings',
  Help: '/help', // RW-655: in-platform help center
  Messages: '/messages', search: '/search',
  // RW-991 Connect Extensions (Celebrations folded into the Recognition module)
  'Connect Search': '/connect/search', 'Town Halls': '/townhalls',
  'Ninja FLOW': '/ninja-flow', Notifications: '/notifications',
  // RW-1305 Academy — LMS + Coaching one surface (epic RW-1304); sub-routes
  // (/academy/library, /academy/coaching, …) resolve inside AcademyApp.
  Academy: '/academy',
  // RW-814 Ask Rêve — AI chat assistant (aiApi callable); the admin tab
  // resolves inside AskReveApp, so one nav key covers the module.
  'Ask Rêve': '/ask-reve',
  'Tech Stack': '/tech-stack',
  'Sign Lab': '/lab/sign', // P-RW-40 — without this entry pathToNav() returns '_none' and the page 404s
  // Trainer + admin only — separate route from /admin so trainers can land
  // on a single read-only dashboard without exposing User Management etc.
  'Adoption Dashboard': '/admin/adoption',
  // RW-1026 — /admin/onboarding is retired (removed, not redirected). This mapping
  // routes the dead path to a no-render nav key so it renders nothing instead of
  // falling through to the Admin dashboard. The surface moved to /onboarding.
  'Onboarding Pipeline': '/admin/onboarding',
  'Offboarding': '/admin/offboarding',
  'Agent Roster': '/admin/roster',
  'Recruiting': '/admin/recruiting',
  'Refer an Agent': '/refer-agent',
  'Focus Mode': '/focus',
  'Buyer Agreements': '/buyer-agreements',
  'One Pipeline': '/deals',
  // RW-2329: Coming Soon and Buyer Needs are Connect › Market MODULES, not
  // Deals surfaces (RW-1785/RW-1782 filed them under Connect; the rail already
  // highlights Connect for them). Without their own keys pathToNav fell all the
  // way through to the one-segment '/deals' → 'One Pipeline', so both boards
  // rendered inside the One Pipeline branch wearing Deals chrome — the "I keep
  // jumping into Deals" report. RW-3647 (Clint, 2026-10-21) then moved the
  // URLs too, from /deals/* to /connect/*, so URL, breadcrumb and rail agree.
  // The old /deals/coming-soon/{id} links (notifications, emails, appLinks.js
  // before the move) redirect via LegacyDeepLinkRedirect, tail kept.
  // The /:id record routes need no key of their own — pathToNav's TWO-SEGMENT
  // rule resolves /connect/coming-soon/{id} to this same key, which is how a
  // record keeps its board's nav treatment (see renderMarketScreen below).
  // These two keys are also what MobileNav's drawer rows have always named
  // ('Coming Soon' / 'Buyer Needs'); before this navToPath had no entry for
  // them and both drawer taps landed on the '/home' fallback.
  'Coming Soon': '/connect/coming-soon',
  'Buyer Needs': '/connect/buyer-needs',
  // RW-1642 What's New — auto-generated changelog. /whats-new/:id resolves via
  // pathToNav's one-segment prefix fallback, so the detail page keeps this nav key.
  'WhatsNew': '/whats-new',
  // RW-2896 Email Signatures (Company › Resources since RW-2917; it was
  // /tools/signatures, which LegacyDeepLinkRedirect still forwards). Sub-pages
  // (/designs, /editor, /install, /admin) resolve through pathToNav's
  // one-segment prefix fallback and route inside SignaturesApp.
  'Email Signatures': '/email-signatures',
};
const PATH_TO_NAV = Object.fromEntries(Object.entries(NAV_TO_PATH).map(([k, v]) => [v, k]));
// Deep link aliases — resolve to existing nav items without adding sidebar entries
PATH_TO_NAV['/posts'] = 'Home';
// RW-2305/RW-2307: /connect/coming-soon/:id renders ComingSoonDetail and
// /connect/buyer-needs/:id renders BuyerNeedDetail — the two in-module records
// (see src/lib/boardFocusId.js for the id read). Every nav-highlight check on
// these two paths must accept the :id form, so both tests live here.
const COMING_SOON_BOARD_RE = /^\/connect\/coming-soon(\/[^/]+)?$/; // RW-3647: was /deals/
const BUYER_NEEDS_BOARD_RE = /^\/connect\/buyer-needs(\/[^/]+)?$/;
// RW-971: '/recognition' now resolves to the Recognition module (via NAV_TO_PATH),
// not the People directory's legacy RW-118 tab.

function pathToNav(pathname) {
  // RW-3647: a legacy path (/deals/coming-soon/…, /coming-soon, /tools/…) is
  // only a redirect — LegacyDeepLinkRedirect forwards it. Without this the old
  // /deals/coming-soon would fall to '/deals' → One Pipeline and mount
  // DealDetail for a deal id of "coming-soon" for the frame before it lands.
  if (legacyDeepLinkTarget(pathname)) return '_none';
  // Exact match first
  if (PATH_TO_NAV[pathname]) return PATH_TO_NAV[pathname];
  // Two-segment prefix for nested admin detail routes (e.g. /admin/deals/abc → Deals),
  // so a deal-detail page highlights "Deals" — not the generic /admin → Admin fallback.
  const seg2 = '/' + pathname.split('/').slice(1, 3).join('/');
  if (PATH_TO_NAV[seg2]) return PATH_TO_NAV[seg2];
  // Prefix match for nested routes (e.g. /listings/abc → Listings)
  const segment = '/' + (pathname.split('/')[1] || '');
  if (PATH_TO_NAV[segment]) return PATH_TO_NAV[segment];
  // Truly unmatched → return a sentinel that doesn't collide with any real nav key.
  // IMPORTANT: do NOT fall through to 'Home' — that caused unmapped routes to render
  // the Feed on top of whatever else was rendering (e.g. /ninja-flow bug).
  return '_none';
}

function navToPath(nav) {
  return NAV_TO_PATH[nav] || '/home';
}

function getPageTitle(nav, pathname = '') {
  // RW-3410: every Deals page shares one nav key ("One Pipeline") — name each
  // from its path instead (a deal page sets its address once it loads)
  if (pathname.startsWith('/deals/')) {
    const page = getHeaderTitle(pathname);
    if (page) return `Rêve | ${page}`;
  }
  if (nav === 'Home') return 'Rêve Realtors';
  // RW-1785: "People" is the internal nav key; the directory's display name
  // is Agents & Staff (Company › People renamed — clients vs. colleagues).
  if (nav === 'People') return 'Rêve | Agents & Staff';
  // RW-3467: likewise "My Tasks" is the internal nav key for /my-tasks, which
  // the rail and the page call Other Tasks (RW-1603) — "My Tasks" is Deals'.
  if (nav === 'My Tasks') return 'Rêve | Other Tasks';
  // RW-3578: the nav key is lowercase "search" (activeNav === 'search' renders it).
  if (nav === 'search') return 'Rêve | Search';
  return `Rêve | ${nav}`;
}

/* Map current pathname → header page title. Handles dynamic :id routes. */
function getHeaderTitle(pathname) {
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname === '/my-tasks') return 'Other Tasks'; // RW-3467: the rail's name
  if (/^\/my-deals\/[^/]+$/.test(pathname)) return 'Deal Detail';
  if (pathname === '/feed') return 'Feed';
  if (pathname === '/groups') return 'Groups';
  if (/^\/groups\/[^/]+$/.test(pathname)) return 'Groups'; // RW-2242: /groups/:id
  if (pathname === '/messages') return 'Messages';
  if (pathname === '/listings') return 'Listings';
  if (pathname === '/listings/new') return 'New Listing';
  if (/^\/listings\/[^/]+$/.test(pathname)) return 'Listing Detail';
  if (pathname === '/flipbooks') return 'Flipbooks';
  if (pathname.startsWith('/flipbooks/')) return 'Flipbook';
  if (pathname === '/buyers') return 'Buyers';
  if (/^\/buyers\/[^/]+$/.test(pathname)) return 'Buyer Detail';
  if (/^\/transactions\/[^/]+$/.test(pathname)) return 'Transaction Detail';
  if (pathname.startsWith('/sign')) return 'Rêve Sign';
  if (pathname === '/contacts') return 'Contacts';
  // CON-1 module tabs are Contacts screens; any other /contacts/<seg> is a contact id.
  if (/^\/contacts\/(today|relationships|organize|health)$/.test(pathname)) return 'Contacts';
  if (/^\/contacts\/[^/]+$/.test(pathname)) return 'Contact Detail';
  if (pathname === '/calendar') return 'Calendar';
  if (pathname === '/saved') return 'Saved Posts';
  if (pathname === '/connect/search') return 'Search';
  if (pathname === '/search') return 'Search'; // RW-3578: the header read "Rêve Workspace"
  if (pathname === '/townhalls') return 'Town Halls';
  // RW-3486: "FLOW", the rail's name and the module's own breadcrumb. The
  // desktop rail trail already said FLOW on /ninja-flow, so the header read
  // "FLOW" on the first screen and "Ninja FLOW" on every sub-screen.
  if (pathname === '/ninja-flow' || pathname.startsWith('/ninja-flow/')) return 'FLOW';
  if (pathname === '/academy' || pathname.startsWith('/academy/')) return 'Academy';
  if (pathname === '/email-signatures' || pathname.startsWith('/email-signatures/')) return 'Email Signatures';
  if (pathname === '/ask-reve' || pathname.startsWith('/ask-reve/')) return 'Ask Rêve';
  if (pathname.startsWith('/posts/')) return 'Feed';
  // RW-3516: /recognition itself too — the phone header read "Rêve Workspace".
  if (pathname === '/recognition' || pathname.startsWith('/recognition/')) return 'Recognition';
  if (pathname.startsWith('/staff/')) return 'StaffHR'; // RW-2248: /staff/<page>, /staff/people/:id
  if (pathname === '/vendors') return 'Vendors';
  if (pathname === '/people') return 'Agents & Staff';
  if (/^\/people\/[^/]+$/.test(pathname)) return 'Profile';
  if (pathname === '/offices') return 'Offices';
  if (pathname === '/org-chart') return 'Org Chart';
  if (pathname === '/events') return 'Events';
  if (pathname === '/events/mine') return 'My Events';
  if (pathname === '/events/new') return 'Create Event';
  if (/^\/events\/[^/]+$/.test(pathname)) return 'Event Detail';
  if (pathname === '/documents') return 'Documents';
  if (pathname === '/documents/library') return 'Reference Library'; // RW-3535: was the 'Rêve Workspace' fallback
  if (pathname === '/photos') return 'Photos';
  if (/^\/photos\/[^/]+$/.test(pathname)) return 'Photos'; // RW-2242: /photos/:id
  if (pathname === '/training' || pathname.startsWith('/training/')) return 'Training';
  if (pathname === '/tutorials') return 'Tutorials';
  if (pathname === '/whats-new' || pathname.startsWith('/whats-new/')) return "What's New";
  if (pathname === '/onboarding') return 'My Onboarding';
  if (pathname === '/leaderboard') return 'Leaderboard';
  if (pathname === '/my-production') return 'My Production';
  if (pathname === '/notifications') return 'Notifications';
  if (pathname === '/settings') return 'Settings';
  if (pathname === '/help') return 'Help';
  if (pathname === '/tech-stack') return 'Tech Stack';
  if (pathname === '/lab/sign') return 'Sign Lab'; // P-RW-40, super admins only
  if (pathname === '/buyer-agreements') return 'Buyer Agreements';
  if (pathname === '/deals') return 'One Pipeline';
  if (pathname === '/deals/tasks') return 'My Deal Tasks';
  if (pathname === '/deals/home') return 'Deals Home';
  if (pathname === '/deals/new') return 'New Deal';
  if (pathname === '/deals/chat') return 'Start a Deal';
  if (pathname === '/deals/payouts') return 'Payouts';
  if (pathname === '/deals/commissions') return 'Commissions';
  if (pathname === '/deals/review') return 'Broker Review';
  if (COMING_SOON_BOARD_RE.test(pathname)) return 'Coming Soon';
  if (BUYER_NEEDS_BOARD_RE.test(pathname)) return 'Buyer Needs';
  if (pathname === '/deals/people') return 'People';
  if (pathname === '/deals/roster') return 'Roster';
  if (pathname === '/deals/leadership') return 'Leadership';
  if (pathname === '/deals/docs') return 'Documents';
  if (pathname === '/deals/settings') return 'Deals Settings';
  if (/^\/deals\/[^/]+$/.test(pathname)) return 'Deal';
  if (pathname === '/reports') return 'Reports';
  if (pathname === '/my-numbers') return 'My Numbers';
  if (pathname === '/admin/adoption') return 'Adoption Dashboard';
  if (pathname === '/admin/onboarding') return 'Onboarding Pipeline';
  if (pathname === '/admin/offboarding') return 'Offboarding';
  if (/^\/admin\/deals\/[^/]+$/.test(pathname)) return 'Deal Detail';
  if (/^\/admin\/roster\/[^/]+$/.test(pathname)) return 'Agent Profile';
  if (pathname === '/admin/roster') return 'Agent Roster';
  if (pathname === '/admin/recruiting') return 'Recruiting';
  if (pathname === '/refer-agent') return 'Refer an Agent';
  if (pathname === '/focus') return 'Focus Mode';
  if (pathname === '/admin') return 'Admin';
  if (pathname === '/home') return 'Home';
  return 'Rêve Workspace';
}

/* Lazy-load with automatic retry on stale-chunk errors — extracted to
 * src/lib/lazyWithRetry.js so AdminDashboard's lazy sections share it (RW-859). */
const Feed = lazyWithRetry(() => import('./components/Feed'));
const SavedPosts = lazyWithRetry(() => import('./components/SavedPosts'));
const FeedLayout = lazyWithRetry(() => import('./components/FeedLayout'));
const TechStackPage = lazyWithRetry(() => import('./components/TechStackPage'));
// P-RW-40: DocuSeal Sign Lab — the lab's only door into the app (signLabIsolation.test.js).
const SignLabPage = lazyWithRetry(() => import('./lab/sign/SignLabPage'));
const PeopleDirectory = lazyWithRetry(() => import('./components/PeopleDirectory'));
const RecognitionModule = lazyWithRetry(() => import('./components/recognition/RecognitionModule'));
const VendorDirectory = lazyWithRetry(() => import('./components/VendorDirectory'));
const EventsApp = lazyWithRetry(() => import('./components/events/EventsApp'));
const DocumentsPage = lazyWithRetry(() => import('./components/DocumentsPage'));
const PhotosPage = lazyWithRetry(() => import('./components/PhotosPage'));
const Groups = lazyWithRetry(() => import('./components/Groups'));
const OnboardingChecklist = lazyWithRetry(() => import('./components/OnboardingChecklist'));
// RW-1442: per-route fallback for the lazy onboarding sub-views. Each route
// conditional mounts its own FRESH Suspense boundary, so React Router v7's
// startTransition shows this immediately instead of holding the previous page
// on screen while the lazy chunk downloads (the audit's "Status tab needs a
// second click" / "View Checklist does nothing" symptom). References no
// imports at module level (TDZ Gotcha #11).
const ObRouteFallback = () => (
  <div style={{ padding: 60, textAlign: 'center', color: '#9CA3AF', fontFamily: "'Jost', sans-serif" }}>Loading…</div>
);
// RW-990 / RW-989: agent-facing intake form + self-status page
const AgentInfoForm = lazyWithRetry(() => import('./components/onboarding/AgentInfoForm'));
const SelfStatus = lazyWithRetry(() => import('./components/onboarding/SelfStatus'));
const SuccessScreen = lazyWithRetry(() => import('./components/onboarding/SuccessScreen'));
const LrecTransferTask = lazyWithRetry(() => import('./components/onboarding/LrecTransferTask'));
const TrainingPage = lazyWithRetry(() => import('./components/TrainingPage'));
const TrainingCatalog = lazyWithRetry(() => import('./components/TrainingCatalog'));
const TutorialsPage = lazyWithRetry(() => import('./components/TutorialsPage'));
// RW-1642 What's New — auto-generated changelog (agent log + entry detail).
const WhatsNewPage = lazyWithRetry(() => import('./components/whatsnew/WhatsNewPage'));
const WhatsNewDetail = lazyWithRetry(() => import('./components/whatsnew/WhatsNewDetail'));
const WhatsNewSpotlight = lazyWithRetry(() => import('./components/whatsnew/WhatsNewSpotlight'));
const HelpPage = lazyWithRetry(() => import('./components/HelpPage')); // RW-655
const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard'));
const AdoptionDashboard = lazyWithRetry(() => import('./components/AdoptionDashboard'));
const ReportsPage = lazyWithRetry(() => import('./components/reports/ReportsPage'));
const MyNumbersPage = lazyWithRetry(() => import('./components/mynumbers/MyNumbersPage'));
const SearchPage = lazyWithRetry(() => import('./components/SearchPage'));
// RW-991 Connect Extensions
const SearchConnect = lazyWithRetry(() => import('./components/connect/SearchConnect'));
const TownHalls = lazyWithRetry(() => import('./components/connect/TownHalls'));
const Settings = lazyWithRetry(() => import('./components/Settings'));
const Messages = lazyWithRetry(() => import('./components/chat/ChatDrawer'));
const ProfileEditor = lazyWithRetry(() => import('./components/ProfileEditor'));
const OrgChart = lazyWithRetry(() => import('./components/OrgChart'));
const ProductionDashboard = lazyWithRetry(() => import('./components/ProductionDashboard'));
const Leaderboard = lazyWithRetry(() => import('./components/Leaderboard'));
const SignApp = lazyWithRetry(() => import('./components/sign/SignApp')); // RW-494 Rêve Sign
const ReferenceLibrary = lazyWithRetry(() => import('./components/ReferenceLibrary'));
const ListingsPage = lazyWithRetry(() => import('./components/ListingsPage'));
const CreateListingWizard = lazyWithRetry(() => import('./components/CreateListingWizard'));
const ListingDetail = lazyWithRetry(() => import('./components/ListingDetail'));
const BuyersPage = lazyWithRetry(() => import('./components/BuyersPage'));
const BuyerDetail = lazyWithRetry(() => import('./components/BuyerDetail'));
// CON-1 (RW-1114): all /contacts* routes resolve inside the module shell
// (Directory, plus the Today/Relationships/Organize/Book Health tabs and
// /contacts/:id detail).
// This used to read "Directory = the existing ContactsPage", which is stale:
// CON-3 (RW-1116) replaced it, and ContactsApp.js:22 imports ContactsDirectory
// — ContactsPage.js has zero importers and no render path at all. It is kept
// per Rule 9, not because anything renders it. Same failure mode as the RW-1792
// note below: a render-path comment that outlived the code it described.
const ContactsApp = lazyWithRetry(() => import('./components/contacts/ContactsApp'));
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const NotificationsPage = lazyWithRetry(() => import('./components/NotificationsPage'));
const OfficeDirectory = lazyWithRetry(() => import('./components/OfficeDirectory'));
const Calendar = lazyWithRetry(() => import('./components/Calendar'));
const MyTasks = lazyWithRetry(() => import('./components/MyTasks'));
// RW-1291: /ninja-flow* now renders the NinjaFlow module (rail + deep-linkable
// screens). RW-1792: this used to add "FlowScorecard.js remains only for
// TrainingPage's embedded view", which was wrong twice over and dangerous once.
// FlowScorecard is LIVE — NinjaFlow/Consolidated.js:32 renders it embedded — so
// anyone trimming dead code on that sentence's authority would have deleted a
// component in active use. TrainingPage, meanwhile, has no render path at all
// (its only code reference is the lazy declaration below); it is kept per Rule 9,
// not because anything embeds it.
const NinjaFlow = lazyWithRetry(() => import('./components/NinjaFlow'));
const AcademyApp = lazyWithRetry(() => import('./components/academy/AcademyApp'));
const SignaturesApp = lazyWithRetry(() => import('./components/tools/signatures/SignaturesApp')); // RW-2896
// RW-814 Ask Rêve — AI chat assistant module (chat + isAdmin-only admin tab).
const AskReveApp = lazyWithRetry(() => import('./components/ai/AskReveApp'));
const WireView = lazyWithRetry(() => import('./components/WireView'));
const DealStatusPage = lazyWithRetry(() => import('./components/DealStatusPage'));
// RW-1610 Flipbooks — branded listing presentations. The public share viewer
// is a separate chunk so an unauthenticated client never downloads the editor.
const FlipbookApp = lazyWithRetry(() => import('./components/flipbook/FlipbookApp'));
const FlipbookPublicPage = lazyWithRetry(() => import('./components/FlipbookPublicPage'));
const Unsubscribe = lazyWithRetry(() => import('./components/Unsubscribe'));
const DocLinkPage = lazyWithRetry(() => import('./components/DocLinkPage')); // RW-3024 part 3
const ConnectApp = lazyWithRetry(() => import('./components/connect/ConnectApp'));
// RW-1026 — unified /onboarding host: staff/ops see this management rail in place
// of the new-hire checklist (role switch in the render below).
const AdminOnboardingTab = lazyWithRetry(() => import('./components/AdminOnboardingTab'));
const OffboardingPipeline = lazyWithRetry(() => import('./components/OffboardingPipeline'));
const RecruitingPage = lazyWithRetry(() => import('./components/RecruitingPage'));
const DealDetail = lazyWithRetry(() => import('./components/DealDetail'));
const BuyerBrokerAgreements = lazyWithRetry(() => import('./components/BuyerBrokerAgreements'));
const DealsPipeline = lazyWithRetry(() => import('./components/DealsPipeline'));
const DealsMyTasks = lazyWithRetry(() => import('./components/DealsMyTasks'));
const DealsHome = lazyWithRetry(() => import('./components/DealsHome'));
const DealsNewDeal = lazyWithRetry(() => import('./components/DealsNewDeal'));
const DealsChatStart = lazyWithRetry(() => import('./components/DealsChatStart'));
const DealsPayouts = lazyWithRetry(() => import('./components/DealsPayouts'));
const DealsCommissions = lazyWithRetry(() => import('./components/DealsCommissions'));
const DealsBrokerReview = lazyWithRetry(() => import('./components/DealsBrokerReview'));
const DealsComingSoon = lazyWithRetry(() => import('./components/DealsComingSoon'));
const ComingSoonDetail = lazyWithRetry(() => import('./components/ComingSoonDetail')); // RW-2305
const DealsBuyerNeeds = lazyWithRetry(() => import('./components/DealsBuyerNeeds'));
const BuyerNeedDetail = lazyWithRetry(() => import('./components/BuyerNeedDetail')); // RW-2307
const DealsPeople = lazyWithRetry(() => import('./components/DealsPeople'));
const DealsRoster = lazyWithRetry(() => import('./components/DealsRoster'));
const DealsLeadership = lazyWithRetry(() => import('./components/DealsLeadership')); // RW-1522
const DealsCompanyDocs = lazyWithRetry(() => import('./components/DealsCompanyDocs'));
const DealsSettings = lazyWithRetry(() => import('./components/DealsSettings'));
const AgentRoster = lazyWithRetry(() => import('./components/AgentRoster'));
const AgentProfileDetail = lazyWithRetry(() => import('./components/AgentProfileDetail'));
const ReferAgent = lazyWithRetry(() => import('./components/ReferAgent'));
const FocusMode = lazyWithRetry(() => import('./components/FocusMode'));
const FocusWidget = lazyWithRetry(() => import('./components/FocusWidget'));
const AgentTurnAlert = lazyWithRetry(() => import('./components/sign/AgentTurnAlert')); // RW-3295
// RW-883b: the switch reads the `compliancePg` flag and renders the Firestore
// queue (default/OFF) or the Postgres complianceApi queue (ON). Reversible seam.
const NotFound = lazyWithRetry(() => import('./components/NotFound'));
// RW-1046/1047 — Staff · HR module (separate full-screen admin workspace).
const StaffApp = lazyWithRetry(() => import('./components/staff/StaffApp'));
const StaffSelfService = lazyWithRetry(() => import('./components/staff/StaffSelfService'));
// RW-899 stage 1 — landing screen for winding-down (offboarding) agents.
const WindDownScreen = lazyWithRetry(() => import('./components/WindDownScreen'));


const IS_CONNECT = window.location.hostname === 'connect.reverealtors.com' || window.location.hostname === 'reve-connect.web.app';

function FeedSkeleton() {
  const shimmer = {
    background: 'linear-gradient(90deg, #F6D9D4 25%, #FDF0EE 50%, #F6D9D4 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  };
  const card = {
    background: '#fff',
    border: '1px solid #F0E9E8',
    borderRadius: 12,
    padding: '20px 20px 16px',
    marginBottom: 16,
  };
  const row = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 };
  const avatar = { ...shimmer, width: 40, height: 40, borderRadius: '50%', flexShrink: 0 };
  const lineShort = { ...shimmer, height: 13, borderRadius: 6, width: '35%', marginBottom: 6 };
  const lineLong = { ...shimmer, height: 13, borderRadius: 6, width: '55%' };
  const body1 = { ...shimmer, height: 14, borderRadius: 6, width: '90%', marginBottom: 8 };
  const body2 = { ...shimmer, height: 14, borderRadius: 6, width: '75%', marginBottom: 8 };
  const body3 = { ...shimmer, height: 14, borderRadius: 6, width: '60%' };

  const SkeletonCard = () => (
    <div style={card}>
      <div style={row}>
        <div style={avatar} />
        <div style={{ flex: 1 }}>
          <div style={lineShort} />
          <div style={lineLong} />
        </div>
      </div>
      <div style={body1} />
      <div style={body2} />
      <div style={body3} />
    </div>
  );

  return (
    <div style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px' }}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeNav = pathToNav(location.pathname);
  const setActiveNav = useCallback((nav) => {
    navigate(navToPath(nav));
  }, [navigate]);

  // Update page title on route change
  useEffect(() => { document.title = getPageTitle(activeNav, location.pathname); }, [activeNav, location.pathname]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState(null);
  const { tenantId } = useTenant();
  const { totalUsers, allUsers } = useSharedUsers();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false,
  );
  // notifications state + subscription removed — handled by NotificationBell,
  // which owns its own open state and closes itself on outside click.
  // RW-2564: the sign-in screen's state. `authError` is a shape, not a string —
  // the two failures it can report read as different screens (a named account
  // that was refused vs. a popup that never finished), and the `reason` sent to
  // LOGIN_FAILED is unchanged. `signedInName` holds the hand-off between the
  // popup returning and onAuthStateChanged propagating; `signedOutAt` turns the
  // bare sign-in card into a sign-out confirmation.
  const [authError, setAuthError] = useState(null); // { kind: 'domain'|'popup'|'generic', email?, code? }
  const [authBusy, setAuthBusy] = useState(false);
  const [signedInName, setSignedInName] = useState(null);
  const [signedOutAt, setSignedOutAt] = useState(null);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1400);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState(null); // { percent, instanceId } for banner
  // RW-1634 T1-13: the agent finished onboarding — onboardingProgress stays null
  // (banner/badges/seen-tracking semantics unchanged) but the sidebar keeps a
  // permanent "Onboarding recap" entry to the /onboarding completion view.
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [onboardingBannerDismissed, setOnboardingBannerDismissed] = useState(false);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  // searchQuery now handled by SearchPage via URL params
  // allUsers removed — SearchPage loads its own data
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  // commentIndex removed — SearchPage loads its own data
  const [alertBannerVisible, setAlertBannerVisible] = useState(false);
  // RW-175: track top-stacked banners so the SW update banner and the
  // notification permission banner can stack under impersonation/event-alert
  // and the header offset accounts for them.
  const [swUpdateBannerVisible, setSwUpdateBannerVisible] = useState(false);
  const [notifBannerVisible, setNotifBannerVisible] = useState(false);

  // RW-1789: every route arrival, tagged with how it was reached. The rail
  // and search mark a one-shot source (navSource.js); deep links, browser
  // back/forward and in-app links read as 'other' — that split is what tells
  // us whether a destination is filed where people look for it.
  // NOTE (RW-1791): must stay BELOW the `user` useState — a dependency array is
  // an ordinary argument, evaluated during render, so reading `user` above its
  // declaration throws a TDZ ReferenceError (Gotcha #11).
  useEffect(() => {
    if (!user?.uid) return;
    track(EVENTS.NAV_ROUTE_REACHED, { route: location.pathname, via: consumeNavSource() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.uid]);

  // ── Role-based access (RW-11) ─────────────────────────────────────────
  // Subscribe to the current user's `role` field. The Firebase Auth custom
  // claim (`auth.token.role`) is the source of truth for server-side rules;
  // this Firestore mirror is the fast UI read.
  // null = role not yet resolved (do NOT assume 'agent'). roleLoaded flips true
  // once the user doc snapshot has been read at least once, so we can tell the
  // brief self-create→trigger gap (loaded, no roles yet) apart from initial load.
  const [userRole, setUserRole] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);
  // RW-702: bump after server-side provisioning to force the role subscription
  // to re-attach with the freshly-issued (tenantId) token.
  const [provisionTick, setProvisionTick] = useState(0);
  // RW-2564: did initializeMyRole actually succeed? `provisionTick` bumps in a
  // `finally` so the listener re-attaches either way — that cannot stand in for
  // success on a screen that tells the agent "Roles applied".
  const [roleProvisioned, setRoleProvisioned] = useState(false);
  const provisionAttemptedRef = useRef(null); // holds the uid we've provisioned
  const [userOffice, setUserOffice] = useState('');
  // RW-1959: canonical office id (RW-475/RW-454) — the retired office/primaryOffice
  // pair is absent on most user docs, so consumers must key on officeId.
  const [userOfficeId, setUserOfficeId] = useState('');
  // RW-2957: who this person is to the posts read rule — { staff, officeId }
  // from the SAME users/{uid} doc the rule reads. null until that doc loads:
  // every posts query waits for it (src/lib/postAudience.js).
  const [userPostViewer, setUserPostViewer] = useState(null);
  const [userNinjaMode, setUserNinjaMode] = useState(false); // RW-220
  // RW-899: agentStatus.status mirror — 'winding_down' (stage 1) gates the client
  const [userAgentStatus, setUserAgentStatus] = useState(null);
  const [userDefaultView, setUserDefaultView] = useState(null); // ROLE-VIEWS: user-chosen landing page
  const [onboardingSeen, setOnboardingSeen] = useState(null); // RW-697 P3: cross-device "new task" baseline { instanceId, itemIds }
  useEffect(() => {
    if (!user?.uid) { setUserRole(null); setRoleLoaded(false); setUserOffice(''); setUserOfficeId(''); setUserPostViewer(null); setUserAgentStatus(null); return undefined; }
    const ref = doc(db, 'users', user.uid);
    return onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      // roles[] is the source of truth; fall back to the legacy single `role`
      // mirror until the migration backfills every doc.
      const rawRoles = Array.isArray(data.roles) && data.roles.length
        ? data.roles
        : (isValidRole(data.role) ? [data.role] : []);
      const valid = rawRoles.filter(isValidRole);
      // Highest-privilege role for the legacy single-value consumers downstream.
      let resolved = valid.length ? (CANONICAL_ROLES.find((r) => valid.includes(r)) || null) : null;
      if (!resolved && (user.email || '').toLowerCase() === PERMANENT_SUPER_ADMIN) {
        // Permanent emergency fallback — never lock Clint out
        resolved = 'super_admin';
      }
      // No silent 'agent' default: leave null when unresolved so the UI shows
      // a "setting up your account" state instead of granting an assumed role.
      setUserRole(resolved);
      setRoleLoaded(true);
      // RW-1959: `officeId` is the canonical office field (RW-475, post-RW-454);
      // `office`/`primaryOffice` are the retired pair, absent on most docs —
      // kept only for the analytics-enrichment consumers (RW-23).
      setUserOffice(data.office || data.primaryOffice || '');
      setUserOfficeId(data.officeId || '');
      setUserPostViewer(postViewerFromUserDoc(data, { email: user.email })); // RW-2957
      setUserNinjaMode(!!data.ninjaMode); // RW-220
      // RW-899 stage 1: 'winding_down' (offboarding started) restricts the
      // client to the Deals module + account basics — see isWindingDown below.
      setUserAgentStatus(data.agentStatus?.status || null);
      setUserDefaultView(data.defaultView || null); // ROLE-VIEWS
      setOnboardingSeen(data.onboardingSeen || null); // RW-697 P3: cross-device "new task" baseline
      // RW-1071: seed the nav-rail theme from the user doc so the chosen rail
      // theme follows the user across devices (Sidebar reads localStorage
      // synchronously at first paint; the event re-themes a live rail).
      try {
        const navTheme = data.navRailTheme;
        if (navTheme && navTheme !== localStorage.getItem('navRailTheme')) {
          localStorage.setItem('navRailTheme', navTheme);
          window.dispatchEvent(new CustomEvent('navrailthemechange', { detail: { theme: navTheme } }));
        }
      } catch { /* private mode / no storage */ }
      // Force a token refresh so the new claim propagates to server-side rules
      try { auth.currentUser?.getIdToken(true).catch(() => {}); } catch {}
    }, () => {
      // Read failed (rules/network). Resolve clint@ via the permanent fallback;
      // otherwise leave the role unresolved rather than assuming 'agent'.
      setUserRole((user.email || '').toLowerCase() === PERMANENT_SUPER_ADMIN ? 'super_admin' : null);
      setRoleLoaded(true);
      // RW-2957: unreadable doc → treat as no office (fail open), as before.
      setUserPostViewer(postViewerFromUserDoc(null, { email: user.email }));
    });
    // provisionTick: re-subscribe after self-provisioning so the listener uses
    // the new token (with tenantId) and can finally read users/{uid}.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.email, provisionTick]);

  // RW-702: server-authoritative first-time provisioning. When a freshly-authed
  // user has no resolvable role (no doc/claim yet — the claimless gap), the
  // client cannot self-create users/{uid} (Tier-4 rule denies a claimless
  // write). Call initializeMyRole (Admin SDK provisions doc + claim), force a
  // token refresh to pull the new tenantId/accessLevel, then re-subscribe.
  //
  // RW-2052: NOT on the Connect host. The IS_CONNECT early return lives far
  // below in the render (the portal is a separate UI), but hooks run before
  // any return — so this effect fired for Connect visitors too, walking an
  // outside-brokerage registrant straight into an internal users/{uid} doc.
  // Connect members are `connectUsers`; the two identity models never meet.
  // The server refuses these callers now regardless (initializeMyRole's
  // eligibility gate) — this just stops asking.
  useEffect(() => {
    if (IS_CONNECT) return;                                    // RW-2052
    if (!user?.uid || !roleLoaded || userRole) return;       // only in the gap
    if (provisionAttemptedRef.current === user.uid) return;    // once per uid
    provisionAttemptedRef.current = user.uid;
    (async () => {
      try {
        await httpsCallable(functions, 'initializeMyRole')();
        await auth.currentUser?.getIdToken(true);
        setRoleProvisioned(true);
      } catch (e) {
        console.warn('[RW-702] self-provision failed', e?.message || e);
      } finally {
        setProvisionTick((t) => t + 1); // re-attach the role listener with the fresh token
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, roleLoaded, userRole]);

  // RW-899 stage 1 — two-stage offboarding. When an offboarding is STARTED the
  // server flips agentStatus.status to 'winding_down': the agent keeps sign-in
  // but the app restricts them to the Deals/transactions module plus account
  // basics (Settings, the notification bell, sign-out) so they can close out
  // their business. This is a WORKFLOW restriction (client-gated), NOT a
  // security boundary — stage 2 ('terminated' → the onUserLifecycle trigger)
  // is where access is actually revoked. Staff/admin accounts are never gated:
  // an admin previewing an offboarding must not lock themselves out, and a
  // winding-down account that still holds any staff role keeps its staff
  // surfaces — the gate applies only to pure agent accounts.
  const isWindingDown = useMemo(() => {
    if (userAgentStatus !== 'winding_down') return false;
    const f = deriveRoleFlags(userRole);
    return !(f.isAdmin || f.isSuperAdmin || f.isTrainer || f.isTC
      || f.isAgentServices || f.isBroker || f.isOfficeCoordinator);
  }, [userAgentStatus, userRole]);

  // RW-2867: read here (above the login redirect, which needs it for a TC's
  // landing) — a hook's deps may not name a const declared further down.
  const { flags: featureFlags, loaded: flagsLoaded } = useFeatureFlags();

  // ROLE-VIEWS: Smart login redirect — user preference > role default.
  // Only fires once per session and only when landing on `/` (not deep links).
  useEffect(() => {
    if (location.pathname !== '/') return;
    if (!user || loading) return;
    // Wait until the role resolves before choosing a landing route — don't
    // redirect based on an assumed role during the setup gap.
    if (!userRole) return;
    // RW-899: winding-down agents stay on '/' — that's the wind-down screen;
    // their role/preference targets are gated anyway.
    if (isWindingDown) return;
    // Compute target route: user preference > role default.
    // RW-2902: a TC's default asks the route guard whether /my-tasks is open
    // — which needs the flags (null until they load: wait). See lib/roleLanding.
    const target = userDefaultView || defaultLandingFor(userRole, { flags: featureFlags, flagsLoaded });
    if (!target) return;
    // RW-942: honor the default-landing-page preference on EVERY landing at `/`,
    // including repeat visits in the same tab session. Previously the
    // "already redirected" path hard-coded /dashboard and silently ignored the
    // user's setting, making it look like the preference didn't work.
    const already = sessionStorage.getItem('reve_hasRedirected');
    if (!already) {
      // RW-693: dev-only — was printing on every prod login (console noise).
      if (process.env.NODE_ENV !== 'production') console.log('[ROLE-VIEWS] Login redirect →', target, '(role:', userRole, ', pref:', userDefaultView, ')');
      sessionStorage.setItem('reve_hasRedirected', '1');
    }
    navigate(target, { replace: true });
  }, [location.pathname, user, loading, userRole, userDefaultView, isWindingDown, navigate, featureFlags, flagsLoaded]);

  const roleFlags = useMemo(() => deriveRoleFlags(userRole), [userRole]);

  const { viewMode, setViewMode, isAgentView } = useViewMode();
  const isRealAdmin = roleFlags.isAdmin;
  const isSuperAdmin = roleFlags.isSuperAdmin;
  const isAdmin = isRealAdmin && !isAgentView; // Respect view mode toggle
  // Trainers get the agent sidebar PLUS access to the Adoption Dashboard.
  // They are NOT considered admin — User Management, Settings, Content
  // Moderation, and impersonation remain admin/super_admin only.
  const isTrainer = roleFlags.isTrainer && !isAgentView;
  // RW-844: the Deals (TC) board mirrors dealsApi `canView` — staff doc-roles,
  // not just admin (TCs are the primary users). admin/trainer respect the
  // agent-view toggle above; tc/agent_services/broker/office_coordinator can't
  // toggle, so they always qualify.
  const canViewDeals = isAdmin || isTrainer || roleFlags.isTC || roleFlags.isAgentServices
    || roleFlags.isBroker || roleFlags.isOfficeCoordinator;
  // RW-845: mirror the dealsApi mutation gates (UI show/hide only — the callable
  // is the real filter). manage = TC/agent_services/admin; approve = broker/admin.
  const canManageDeals = canViewDeals && (isAdmin || roleFlags.isTC || roleFlags.isAgentServices);
  const canApproveDealGate = canViewDeals && (isAdmin || roleFlags.isBroker);
  // RW-1543: one roles object for every One Pipeline gate — desktop rail,
  // mobile pill strip, and surfaces all read this so they can't disagree.
  // canReview mirrors complianceApi REVIEW_ROLES (admin|broker|tc — NOT
  // agent_services, who'd pass canManage and then fail every review call).
  const dealsRoles = useMemo(() => ({
    canManage: canManageDeals,
    canReview: isAdmin || roleFlags.isTC || roleFlags.isBroker,
    canLead: canApproveDealGate,
    // RW-2824: fill forms on any deal = manage (admin/TC/agent services) or
    // lead (broker) — mirrors requireDealMember's staff arm.
    canFillForms: canManageDeals || canApproveDealGate,
    canViewDeals,
    // RW-2298: the flag bypass for DealsNav's pill gate (mirrors the rail).
    isAdmin,
    // RW-3878: DealsNav applies the wind-down allowlist like the rail does.
    windingDown: isWindingDown,
  }), [canManageDeals, isAdmin, roleFlags.isTC, roleFlags.isBroker, canApproveDealGate, canViewDeals, isWindingDown]);

  // RW-187: Super-admin "Login As" — client-side impersonation. Read-only UI
  // components see `effectiveUser` (the impersonated agent) so the super
  // admin can view the agent's exact dashboard, feed, sidebar, and role-gated
  // menu items. Writes still use the real `user` / `auth.currentUser`.
  const { impersonatedUser, isImpersonating, stopImpersonation } = useImpersonation();

  // Augment the auth `user` with the resolved role so existing prop-drilled
  // components (ChecklistPanel, ContactsPage, etc.) can read `user.role`
  // directly without needing their own user-doc subscription.
  // Shape and field semantics live in src/lib/userWithRole.js (RW-1959).
  const userWithRole = useMemo(() => buildUserWithRole(user, {
    role: userRole,
    office: userOffice,
    officeId: userOfficeId,
    ninjaMode: userNinjaMode,
    agentStatus: userAgentStatus,
    postViewer: userPostViewer,
  }), [user, userRole, userOffice, userOfficeId, userNinjaMode, userAgentStatus, userPostViewer]);

  // Effective user for read-only UI surfaces (Dashboard, Feed, Sidebar,
  // Profile). When impersonating, we substitute the impersonated agent's doc
  // so role-gated menus and "my data" views reflect what THEY would see.
  const effectiveUser = useMemo(() => {
    if (!isImpersonating || !impersonatedUser) return userWithRole;
    return {
      uid: impersonatedUser.uid,
      email: impersonatedUser.email || '',
      displayName: impersonatedUser.displayName || '',
      photoURL: impersonatedUser.photoURL || '',
      role: impersonatedUser.role || 'agent',
      office: impersonatedUser.office || impersonatedUser.primaryOffice || '',
      // RW-1959: canonical office id — same contract as userWithRole.
      officeId: impersonatedUser.officeId || '',
      // RW-2957: the agent's own post audience, so "Login As" shows their feed.
      // Firestore still sees the super admin (staff), so any scope is allowed.
      postViewer: postViewerFromUserDoc(impersonatedUser),
      // Marker so a child component can defensively detect impersonation if needed.
      _impersonated: true,
    };
  }, [isImpersonating, impersonatedUser, userWithRole]);

  // RW-1026 — unified onboarding host switch. Staff/ops see the management rail
  // at /onboarding; new hires (and any staff using the "view as agent" toggle)
  // see the new-hire checklist. canSeeStaffOnboardingNav reads the real role;
  // gating on !isAgentView makes the global toggle a "view as new hire" preview.
  const isStaffOnboarding = !isAgentView && canSeeStaffOnboardingNav(userWithRole);

  // isAdmin for read-only UI must reflect the IMPERSONATED role so the agent's
  // sidebar/menu visibility lines up with what they'd actually see.
  const effectiveIsAdmin = useMemo(() => {
    if (!isImpersonating) return isAdmin;
    const r = impersonatedUser?.role;
    return r === 'admin' || r === 'super_admin';
  }, [isImpersonating, impersonatedUser, isAdmin]);
  // Same idea for trainer — when impersonating a trainer, surface the
  // trainer-specific sidebar (which includes the Adoption Dashboard).
  const effectiveIsTrainer = useMemo(() => {
    if (!isImpersonating) return isTrainer;
    return impersonatedUser?.role === 'trainer';
  }, [isImpersonating, impersonatedUser, isTrainer]);
  // RW-844: surface the Deals nav for the impersonated staff role too.
  const effectiveCanViewDeals = useMemo(() => {
    if (!isImpersonating) return canViewDeals;
    const f = deriveRoleFlags(impersonatedUser?.role);
    return f.isAdmin || f.isTrainer || f.isTC || f.isAgentServices || f.isBroker || f.isOfficeCoordinator;
  }, [isImpersonating, impersonatedUser, canViewDeals]);
  const sidebarWidth = useSidebarWidth();
  const [overdueCount, setOverdueCount] = useState(0);
  const [ownedTaskRows, setOwnedTaskRows] = useState([]); // RW-2867: the badge's other-task half
  const [requiredTrainingCount, setRequiredTrainingCount] = useState(0);
  const [appToast, setAppToast] = useState('');
  const isOnline = useOnlineStatus();
  const [offlineDismissed, setOfflineDismissed] = useState(false);
  // Reset dismiss when going offline again; show "Back online!" toast on reconnect
  const wasOfflineRef = React.useRef(false);
  useEffect(() => {
    if (!isOnline) { setOfflineDismissed(false); wasOfflineRef.current = true; }
    if (isOnline && wasOfflineRef.current) {
      wasOfflineRef.current = false;
      setAppToast('Back online!');
      setTimeout(() => setAppToast(''), 3000);
    }
  }, [isOnline]);

  // RW-175: expose the header bottom as a CSS custom property so child
  // components (e.g. FlowScorecard's sticky progress bar) can stick directly
  // below the header without needing to know the banner stack heights.
  // Placed here (after useImpersonation) so `isImpersonating` is in scope.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const ALERT_H = 48; const IMPERSONATION_H = 40; const SW_UPDATE_H = 44; const NOTIF_BANNER_H = 48;
    const stack =
      (alertBannerVisible ? ALERT_H : 0) +
      (isImpersonating ? IMPERSONATION_H : 0) +
      (swUpdateBannerVisible ? SW_UPDATE_H : 0) +
      (notifBannerVisible ? NOTIF_BANNER_H : 0);
    const headerBottom = stack + 56;
    // RW-239: include iOS safe-area so sticky children sit below the notch.
    document.documentElement.style.setProperty('--reve-header-bottom', `calc(${headerBottom}px + var(--reve-safe-top))`);
    return () => {
      document.documentElement.style.removeProperty('--reve-header-bottom');
    };
  }, [alertBannerVisible, isImpersonating, swUpdateBannerVisible, notifBannerVisible]);

  // Route protection: if an agent tries to hit a disabled module URL, bounce to dashboard.
  // Only re-evaluate on pathname changes — `featureFlags` is intentionally read fresh
  // but NOT a dep, so a snapshot tick (which produces a new object reference even when
  // values are unchanged) can't bounce a user mid-action after creating a record.
  // RW-3406: and only once the role is known — an admin bypasses flags, and
  // isAdmin is false until the user doc arrives on a cold load.
  useEffect(() => {
    const decision = routeGuardDecision({
      path: location.pathname, signedIn: !!user, flagsLoaded, roleLoaded, flags: featureFlags, isAdmin,
    });
    if (decision === 'bounce') {
      setAppToast('This feature is not available yet.');
      setTimeout(() => setAppToast(''), 3000);
      navigate('/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user, flagsLoaded, roleLoaded, isAdmin, navigate]);

  // RW-899 stage 1 route gate: a winding-down agent is restricted to the
  // Deals/transactions module + account basics (src/lib/windDown.js is the
  // single allowlist, shared with the Sidebar filter). Everything else lands
  // on the wind-down screen at '/'. Client-side WORKFLOW gate only — stage 2
  // ('terminated' → onUserLifecycle) is the real security boundary.
  useEffect(() => {
    if (!user || !isWindingDown) return;
    if (isWindDownAllowedPath(location.pathname)) return;
    navigate('/', { replace: true });
  }, [location.pathname, user, isWindingDown, navigate]);

  // Initialize analytics once authenticated. Pass the role-augmented user so
  // the buffered Firestore queue auto-enriches every event with role/office.
  // RW-23: re-init when role/office land from the user-doc snapshot so the
  // first events to fire after login carry the correct enrichment.
  useEffect(() => {
    if (!user?.uid) return;
    // RW-2351: pass isAgentView — NEVER ViewModeContext's isAdminView, which is
    // true by DEFAULT for everyone and would gate out the whole user base.
    // RW-2379: accessLevel is the PRIVILEGE field (roleFlags derives it from
    // roles[]); `userWithRole.role` is roles[0], a label, and must not be used
    // for this. It tags events as internal — it does NOT gate them.
    initAnalytics(userWithRole, { isSimulatedView: isAgentView, accessLevel: roleFlags.accessLevel });
    // If handleLogin just completed a fresh sign-in, fire LOGIN_SUCCESS now
    // (after initAnalytics, so the queue accepts it). The flag is set in
    // handleLogin and consumed exactly once.
    try {
      if (sessionStorage.getItem('reve_just_logged_in') === '1') {
        sessionStorage.removeItem('reve_just_logged_in');
        track(EVENTS.LOGIN_SUCCESS, { method: 'google_sso' });
      }
    } catch { /* sessionStorage may be unavailable */ }
  }, [user?.uid, userRole, userOffice, isAgentView, roleFlags.accessLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track page views on route changes
  useEffect(() => {
    if (!user?.uid) return;
    // RW-3647: a legacy path is a redirect, not a visit — the page it lands on
    // logs its own view. (Old /deals/coming-soon would otherwise count as a
    // deal record.)
    if (legacyDeepLinkTarget(location.pathname)) return;
    trackEvent('page_view', { page: location.pathname });
    logPageView(user.uid, location.pathname);
  }, [location.pathname, user?.uid]);

  // Auto-close chat drawer when navigating to the full /messages page
  // (avoids two message UIs being open simultaneously)
  useEffect(() => {
    if (location.pathname.startsWith('/messages')) setChatDrawerOpen(false);
  }, [location.pathname]);

  // RW-2588: no Contacts badge here any more. The book-size count rode on the
  // Deals › People › Contacts row; Contacts is now a standalone rail entry
  // with no badge (a permanent "99+"), and ContactsApp fetches its own count.

  // Sidebar badge: overdue tasks for the current user (live)
  // RW-440: CG tasks read is owner/role-gated. subscribeOwnedTasks runs the two
  // owner-scoped queries (createdBy / assignedTo.userId == uid) merged — so the
  // returned rows are already the current user's tasks (no client owner filter
  // needed); the previous unfiltered collectionGroup('tasks') read is now denied.
  useEffect(() => {
    if (!user?.uid) { setOverdueCount(0); setOwnedTaskRows([]); return undefined; }
    return subscribeOwnedTasks(db, {
      user,
      onResult: (rows) => {
        setOwnedTaskRows(rows);
        const now = new Date(); now.setHours(0, 0, 0, 0);
        let count = 0;
        rows.forEach((t) => {
          if (t.archived) return;
          // RW-3801: a task I created for someone else is theirs, not my overdue.
          if (isDelegatedByMe(t, user.uid)) return;
          if (['completed', 'complete', 'approved', 'waived', 'na'].includes(t.status)) return;
          const due = t.dueDate?.toDate?.() || (t.dueDate ? new Date(t.dueDate) : null);
          if (due && due < now) count++;
        });
        setOverdueCount(count);
      },
    });
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // RW-2867 (RW-2857 decision C): My Tasks' badge counts all three kinds —
  // what needs you now (lib/taskInbox inboxBadgeCount: your overdue deal,
  // onboarding and other tasks, plus agent ticks waiting on your check). The
  // deal + onboarding half is the inbox read (dealsApi taskInbox), polled
  // every 5 minutes, on window focus, and the moment a screen changes a task;
  // the other tasks are the rows the listener above already holds (no new
  // listener). Only while My Tasks is open to this user (the route guard's rule).
  const taskInboxOpen = !!user?.uid && flagsLoaded && isPathEnabled(TASK_INBOX_PATH, featureFlags, isAdmin);
  const { dealRows: inboxDealRows, onboardingRows: inboxOnboardingRows } = useTaskInboxRead({ enabled: taskInboxOpen, pollMs: 5 * 60 * 1000 });
  const tasksDueCount = useMemo(() => {
    if (!taskInboxOpen) return 0;
    const today = localYmd();
    return inboxBadgeCount([
      ...inboxDealRows.map(fromDealRow),
      ...inboxOnboardingRows.map(fromOnboardingRow),
      ...ownedTaskRows.map((t) => fromOtherTask(t, { uid: user?.uid, today })).filter(Boolean),
    ], today);
  }, [taskInboxOpen, inboxDealRows, inboxOnboardingRows, ownedTaskRows, user?.uid]);

  // RW-219: Required training badge count
  useEffect(() => {
    if (!user?.uid) { setRequiredTrainingCount(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const { collection: col, onSnapshot: onSnap, query: q, where: wh } = await import('firebase/firestore');
        const qy = q(col(db, 'courses'), wh('published', '==', true), wh('required', '==', true));
        const unsub = onSnap(qy, async (snap) => {
          if (cancelled) return;
          let incomplete = 0;
          for (const courseDoc of snap.docs) {
            try {
              const { doc: docRef, getDoc: gd } = await import('firebase/firestore');
              const enrollSnap = await gd(docRef(db, 'courses', courseDoc.id, 'enrollments', user.uid));
              if (!enrollSnap.exists() || enrollSnap.data()?.status !== 'completed') incomplete++;
            } catch { incomplete++; }
          }
          if (!cancelled) setRequiredTrainingCount(incomplete);
        }, () => {});
        return () => { cancelled = true; unsub(); };
      } catch (e) { console.warn('training badge query failed', e?.message); }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // RW-2268 (F8): the 'unreadImportant' badge on Connect › Social › Important
  // Posts. `navigation.js` has declared that badgeKey since RW-1788 and nothing
  // ever supplied it — the paired-registry class RW-2155 named: DEFINED in one
  // list, REACHED through another, so the row silently never badges.
  //
  // It costs nothing to supply: App already holds `posts` (it feeds the stream,
  // the search bar and the directory), and `isStickyPost` is the same rule
  // Feed.js and FeedLayout.js pin their "Requires your attention" set with. So
  // the badge and the screen it links to can never disagree — important or
  // critical, written by a human, not yet acknowledged by me, inside the 7-day
  // window. No new query, no new listener (Rule 25).
  const unreadImportantCount = useMemo(() => {
    // RW-2986: a scheduled post is not on the stream yet (Feed.js), so it is not in the badge either.
    return countAttentionPosts(posts, user?.uid);
  }, [posts, user?.uid]);

  // RW-1642 What's New callouts — count pill / NEW chips / spotlight. App owns
  // the state so the Sidebar props and the context provider share one fetch;
  // gated on the whatsNew flag so a dark module makes zero noise.
  const whatsNewEnabled = !!user?.uid && flagsLoaded && isFeatureEnabled('whatsNew', featureFlags, isAdmin);
  const whatsNewCallouts = useWhatsNewCalloutsState(user, whatsNewEnabled);



  // Responsive
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener('change', onChange);
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => { mq.removeEventListener('change', onChange); window.removeEventListener('resize', onResize); };
  }, []);

  // Auth — check profile completion, then track user (sequential to avoid race)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        setAuthError(null);
        // RW-2564: the app is taking over — drop the hand-off screen.
        setSignedInName(null);
        setSignedOutAt(null);
        try {
          // Fast local check — prevents modal flash on reload even if Firestore is slow
          const localDone = localStorage.getItem(`reve_onboarding_done_${currentUser.uid}`) === '1';
          const snap = await getDoc(doc(db, 'users', currentUser.uid));
          const data = snap.data();
          // Admin bypass — never show onboarding for admin accounts
          // RW-11: claim-aware bypass — super_admin/admin skip onboarding.
          // Permanent email fallback for clint@ in case the role doc is missing.
          const adminBypass =
            data?.role === 'super_admin' || data?.role === 'admin' ||
            currentUser.email === PERMANENT_SUPER_ADMIN;
          // RW-184 Phase 2: Persistence fix — Firestore is source of truth.
          // 1. If onboardingComplete/profileComplete is set, never show.
          // 2. If user has a completed onboardingInstance, never show.
          // 3. If account is > 30 days old and no instance, skip (existing agent).
          // 4. Only show for new accounts (< 30 days) without completion flags.
          if (localDone || adminBypass || data?.onboardingComplete === true || data?.profileComplete === true) {
            setShowOnboarding(false);
            // Sync localStorage if Firestore says done but local flag missing
            if (!localDone && (data?.onboardingComplete === true || data?.profileComplete === true)) {
              try { localStorage.setItem(`reve_onboarding_done_${currentUser.uid}`, '1'); } catch {}
            }
          } else if (!snap.exists()) {
            setShowOnboarding(true);
          } else {
            // Check if they have a completed onboarding instance.
            // RW-707: tenantId filter required by query-shape rule (RW-684 pattern).
            // This runs inside the onAuthStateChanged callback (effect deps []),
            // so the reactive `tenantId` would be a stale null closure — read the
            // live registered tenant from the data layer instead, and safely skip
            // the instance check if it hasn't resolved yet (TenantContext sets it
            // on the same auth tick).
            const _tid = _getCurrentTenantId();
            const instanceSnap = _tid ? await getDocs(
              query(collection(db, 'onboardingInstances'), where('tenantId', '==', _tid), where('agentUid', '==', currentUser.uid), limit(1)),
            ) : null;
            // RW-2065: `archived` is a third lifecycle state — an unassigned
            // hire is neither complete nor live, so ask the shared predicate
            // rather than testing status directly.
            const _inst = instanceSnap && !instanceSnap.empty ? instanceSnap.docs[0].data() : null;
            if (_inst && isArchivedInstance(_inst)) {
              // Staff took them off onboarding: no wizard, no nag, no flag write.
              setShowOnboarding(false);
            } else if (_inst && isCompletedInstance(_inst)) {
              // Instance is complete — hide forever. users.onboardingComplete is
              // server-owned (RW-3595: the rules refuse a self-write); the
              // onboarding callables mirror it from the instance.
              setShowOnboarding(false);
            } else {
              // Check account age — if > 30 days old with no completion, skip (existing agent).
              // Default to age 0 (show modal) when we have no signal at all, so a
              // fresh user who abandoned the modal on first login still resumes it
              // on return. firstLoginAt is stamped below so this stabilizes after
              // the first auth tick.
              const firstLogin = data?.firstLoginAt?.toDate?.()
                || data?.joinedAt?.toDate?.()
                || data?.createdAt?.toDate?.()
                || null;
              const accountAge = firstLogin ? (Date.now() - firstLogin.getTime()) / 86400000 : 0;
              if (accountAge > 30) {
                setShowOnboarding(false);
              } else {
                setShowOnboarding(true);
              }
            }
          }
          // RW-269: First-login tracking — stamp firstLoginAt if not already set
          if (!data?.firstLoginAt) {
            setDoc(doc(db, 'users', currentUser.uid), withTenant('users', { firstLoginAt: serverTimestamp() }), { merge: true }).catch(() => {});
          }
        } catch {
          setShowOnboarding(false);
        }
        // Track user AFTER onboarding check — merge: true preserves profileComplete
        setDoc(
          doc(db, 'users', currentUser.uid),
          withTenant('users', {
            displayName: currentUser.displayName || 'Member',
            email: currentUser.email || '',
            photoURL: currentUser.photoURL || '',
            lastSeen: serverTimestamp(),
          }),
          { merge: true },
        ).catch(() => {});
      } else {
        setShowOnboarding(false);
      }
    });
    return unsubscribe;
  }, []);

  // RW-184 Phase 2: Listen for agent's active onboarding instance (for banner)
  useEffect(() => {
    if (!user?.uid) { setOnboardingProgress(null); setOnboardingComplete(false); return undefined; }
    if (!tenantId) return undefined;
    // RW-707: tenantId filter required by query-shape rule (RW-684 pattern)
    const q2 = query(collection(db, 'onboardingInstances'), where('tenantId', '==', tenantId), where('agentUid', '==', user.uid), limit(1));
    const unsub = onSnapshot(q2, (snap) => {
      if (snap.empty) { setOnboardingProgress(null); setOnboardingComplete(false); return; }
      const inst = snap.docs[0].data();
      // RW-2065: an archived record drives no banner and no recap entry — the
      // hire is off onboarding, not finished with it.
      if (isArchivedInstance(inst)) { setOnboardingProgress(null); setOnboardingComplete(false); return; }
      // RW-1634 T1-13: completed instance — no progress object (banner off) but
      // keep the sidebar recap entry alive.
      if (isCompletedInstance(inst)) { setOnboardingProgress(null); setOnboardingComplete(true); return; }
      setOnboardingComplete(false);
      const items = inst.items || [];
      const reqItems = items.filter((i) => i.required);
      const doneReq = reqItems.filter((i) => i.status === 'complete' || i.status === 'waived');
      const percent = reqItems.length > 0 ? Math.round((doneReq.length / reqItems.length) * 100) : 0;
      // RW-697 Phase 3: agent-actionable items still needing the agent — drives
      // the sidebar pending-COUNT badge (design "Onboarding · 3") and new-item
      // detection. Uses the canonical isAgentItem() assignee helper so it honors
      // the same assignTo[]/legacy schema the checklist does (staff-only items
      // don't count — "Tammy handles the rest").
      const agentPending = items.filter((i) => isAgentItem(i) && i.status !== 'complete' && i.status !== 'waived');
      const pendingItemIds = agentPending
        .map((i) => String(i.itemId || i.id || i.title || ''))
        .filter(Boolean);
      setOnboardingProgress({ percent, instanceId: snap.docs[0].id, pending: agentPending.length, pendingItemIds });
    });
    return unsub;
  }, [user?.uid, tenantId]);

  // RW-697 Phase 3: new-task detection. The agent's "seen" baseline lives on
  // the user doc (`onboardingSeen = { instanceId, itemIds }`) so the sidebar
  // "new task" dot is CROSS-DEVICE — viewing the checklist on one device clears
  // the dot everywhere, and a freshly-assigned item (admin adds work after the
  // agent last looked) re-lights it everywhere. `onboardingSeen` is hydrated by
  // the user-doc listener above. Marked whenever the agent is on /onboarding.
  useEffect(() => {
    if (location.pathname !== '/onboarding') return;
    if (!user?.uid || !onboardingProgress?.instanceId) return;
    const ids = onboardingProgress.pendingItemIds || [];
    // Skip the write when the server baseline already matches (avoids a write on
    // every realtime snapshot while the agent sits on the page; the listener
    // re-runs this effect, and this guard makes it converge).
    const sameInstance = onboardingSeen && onboardingSeen.instanceId === onboardingProgress.instanceId;
    const seenIds = sameInstance && Array.isArray(onboardingSeen.itemIds) ? onboardingSeen.itemIds : null;
    const matches = seenIds && seenIds.length === ids.length && ids.every((id) => seenIds.includes(id));
    if (matches) return;
    setDoc(
      doc(db, 'users', user.uid),
      withTenant('users', { onboardingSeen: { instanceId: onboardingProgress.instanceId, itemIds: ids } }),
      { merge: true },
    ).catch(() => {});
  }, [location.pathname, user?.uid, onboardingProgress, onboardingSeen]);

  // Count of newly-assigned agent items not yet seen (drives the "new" dot).
  // Returns 0 until we have a baseline for THIS instance, so the dot only ever
  // flags work added after the agent last viewed — never a cold-start flash on
  // a fresh device (brand-new agents are routed into /onboarding by the Pattern
  // A takeover, which writes the baseline).
  const onboardingNewCount = useMemo(() => {
    if (!onboardingProgress?.pendingItemIds?.length || !onboardingProgress?.instanceId) return 0;
    if (!onboardingSeen || onboardingSeen.instanceId !== onboardingProgress.instanceId) return 0;
    const seenSet = new Set(onboardingSeen.itemIds || []);
    return onboardingProgress.pendingItemIds.filter((id) => !seenSet.has(id)).length;
  }, [onboardingProgress, onboardingSeen]);

  // Posts — limit initial load for fast first paint (RW-627: tenantId-scoped per wave-A)
  // RW-2957: an office reader's stream carries `office in [all…, mine]` — the
  // posts read rule denies the whole query otherwise. Waits for the viewer.
  const { ready: postScopeReady, scope: postScope } = usePostScope({ postViewer: userPostViewer });
  useEffect(() => {
    if (!tenantId || !postScopeReady) return;
    const q = query(
      collection(db, 'posts'),
      where('tenantId', '==', tenantId),
      ...(postScope ? [where('office', 'in', postScope)] : []),
      orderBy('timestamp', 'desc'),
      limit(50),
    );
    return onSnapshot(q, (snap) => {
      setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => { if (err?.code !== 'permission-denied') console.warn('[RW-627] App posts listener failed:', err?.message); }); // RW-477 guard (RW-484)
  }, [tenantId, postScopeReady, postScope]);

  // Total users count — now from UsersProvider (shared subscription)


  // DM unread count (RW-621: tenantId-scoped per wave-A)
  useEffect(() => {
    if (!user || !tenantId) return undefined;
    const q = query(
      collection(db, 'conversations'),
      where('tenantId', '==', tenantId),
      where('participants', 'array-contains', user.uid),
    );
    return onSnapshot(q, (snap) => {
      let total = 0;
      snap.docs.forEach((d) => {
        const data = d.data();
        total += (data.unreadCount && data.unreadCount[user.uid]) || 0;
      });
      setDmUnreadCount(total);
    }, (err) => { if (err?.code !== 'permission-denied') console.warn('[RW-621] App DM unread listener failed:', err?.message); }); // RW-477 guard (RW-484)
  }, [user, tenantId]);

  // unreadCount + markNotificationsRead removed — handled by NotificationBell

  // RW-2564: the popup round trip, one sign-in button. The three outcomes are
  // three screens — accepted (hand off to the app), a Google account that is
  // not ours (name the address that was refused), and a popup that never
  // finished (say that nothing changed). The LOGIN_FAILED `reason` values are
  // the ones analytics already collects, unchanged.
  const handleLogin = async () => {
    setAuthError(null);
    setSignedOutAt(null);
    setAuthBusy(true);
    try {
      const result = await signInWithPopup(auth, provider);
      // Enforce domain restriction — hd parameter is only a UI hint
      const email = result.user?.email || '';
      if (!email.endsWith('@reverealtors.com')) {
        // Domain rejection counts as a failed login for analytics purposes.
        trackWithUser(EVENTS.LOGIN_FAILED, result.user, { reason: 'domain_restricted' });
        await signOut(auth);
        setUser(null);
        setAuthError({ kind: 'domain', email });
        return;
      }
      // Defer the LOGIN_SUCCESS event until initAnalytics() has run with the
      // role-augmented user — see the analytics init useEffect above.
      try { sessionStorage.setItem('reve_just_logged_in', '1'); } catch { /* ignore */ }
      // Hold the hand-off screen for the window between the popup resolving and
      // onAuthStateChanged handing the app a user. Cleared there.
      setSignedInName(firstNameOf(result.user) || '');
    } catch (e) {
      const code = e?.code || 'popup_error';
      trackWithUser(EVENTS.LOGIN_FAILED, null, { reason: code });
      // Blocked, dismissed, or superseded by a second click: all three mean the
      // Google window went away before sign-in finished, and none of them
      // changed anything on the account. Everything else is a real failure.
      const popupCodes = [
        'auth/popup-blocked', 'auth/popup-closed-by-user',
        'auth/cancelled-popup-request', 'auth/user-cancelled',
      ];
      setAuthError({ kind: popupCodes.includes(code) ? 'popup' : 'generic', code });
    } finally {
      setAuthBusy(false);
    }
  };
  const handleLogout = async () => {
    setMenuOpen(false);
    track(EVENTS.LOGOUT);
    // RW-2440: a deal close still waiting out its Undo window is released now,
    // while the agent is still signed in — after signOut it would be refused.
    // It may queue behind a move on the same deal that is still unanswered, so
    // wait long enough for that; once its request is on the wire it carries
    // its token and completes. Capped so sign-out never hangs.
    await Promise.race([
      flushHeldDealCloses().catch(() => {}),
      new Promise((resolve) => { setTimeout(resolve, 10000); }),
    ]);
    await signOut(auth);
    // RW-2564: sign-out lands on a confirmation, not the bare sign-in card.
    setSignedInName(null);
    setAuthError(null);
    setSignedOutAt(new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase());
  };

  // Deal status page (public, token-secured) — RW-218
  if (location.pathname.startsWith('/deal-status/')) {
    return <ErrorBoundary><React.Suspense fallback={<AuthRouteLoader />}><DealStatusPage /></React.Suspense></ErrorBoundary>;
  }

  // RW-1610: shared flipbook (public, token-secured — no login)
  if (location.pathname.startsWith('/flipbook/')) {
    return <ErrorBoundary><React.Suspense fallback={<AuthRouteLoader />}><FlipbookPublicPage /></React.Suspense></ErrorBoundary>;
  }

  // Wire instructions view (public, token-secured)
  if (location.pathname.startsWith('/wire/')) {
    return <ErrorBoundary><React.Suspense fallback={<AuthRouteLoader />}><WireView /></React.Suspense></ErrorBoundary>;
  }

  // RW-3024 part 3: a deal document's download link — no auth (the code is the permission)
  if (location.pathname.startsWith('/d/')) {
    return <ErrorBoundary><React.Suspense fallback={<AuthRouteLoader />}><DocLinkPage pathname={location.pathname} /></React.Suspense></ErrorBoundary>;
  }
  // Unsubscribe page — no auth required
  if (location.pathname === '/unsubscribe') {
    return <ErrorBoundary><React.Suspense fallback={<AuthRouteLoader />}><Unsubscribe /></React.Suspense></ErrorBoundary>;
  }

  // Rêve Connect portal — completely separate UI
  if (IS_CONNECT) {
    return <ErrorBoundary><React.Suspense fallback={<AuthRouteLoader />}><ConnectApp user={userWithRole} loading={loading} /></React.Suspense></ErrorBoundary>;
  }

  // RW-2564: the session check wears the sign-in frame, so nothing about the
  // layout moves when it resolves into the sign-in pane.
  if (loading) {
    return <AuthChecking mobile={mobile} />;
  }

  if (!user) {
    // RW-2564: one Google button and no "how are you joining us?" fork. Connect
    // lives on its own host, so it is a line at the bottom of the pane rather
    // than a choice at the top, and the localStorage flag it set is gone.
    // Which screen shows is a function of how we got here, not a selector.
    if (signedInName !== null) return <AuthHandoff name={signedInName} mobile={mobile} />;
    if (authError?.kind === 'domain') {
      return <AuthRejected email={authError.email} mobile={mobile} onRetry={handleLogin} />;
    }
    if (authError?.kind === 'popup' || authError?.kind === 'generic') {
      return <AuthPopupFailed code={authError.code} mobile={mobile} onRetry={handleLogin} />;
    }
    if (signedOutAt) {
      return <AuthSignedOut at={signedOutAt} busy={authBusy} mobile={mobile} onGoogle={handleLogin} />;
    }
    return <AuthSignIn busy={authBusy} mobile={mobile} onGoogle={handleLogin} />;
  }

  // Role refactor (Step 4): the brief window between the client self-creating
  // the user doc and the onUserCreated trigger writing roles[]/accessLevel.
  // Don't assume a role — show a light setup state until it resolves. clint@ is
  // covered by the permanent fallback so it never lands here. (This also
  // surfaces, rather than masks, any user whose doc has no valid role yet.)
  if (user && roleLoaded && !userRole) {
    // RW-2564: named steps instead of white-on-noir text. `roleProvisioned` is
    // the initializeMyRole callable actually having succeeded, not a timer, so
    // the second step only ticks when roles really were written.
    return (
      <AuthProvisioning
        name={firstNameOf(user)}
        rolesApplied={roleProvisioned}
        mobile={mobile}
        onReload={() => window.location.reload()}
      />
    );
  }

  // RW-175: stack heights for fixed top banners. Order from top:
  //   1) Event alert banner (48px)
  //   2) Impersonation banner (40px)
  //   3) SW update banner (44px)
  //   4) Notification permission banner (48px)
  // Header sits below the entire stack.
  const ALERT_H = 48;
  const IMPERSONATION_H = 40;
  const SW_UPDATE_H = 44;
  const NOTIF_BANNER_H = 48;
  const alertOffset = alertBannerVisible ? ALERT_H : 0;
  const impersonationOffset = alertOffset + (isImpersonating ? IMPERSONATION_H : 0);
  const swUpdateOffset = impersonationOffset + (swUpdateBannerVisible ? SW_UPDATE_H : 0);
  const notifBannerOffset = swUpdateOffset + (notifBannerVisible ? NOTIF_BANNER_H : 0);
  const headerTopOffset = notifBannerOffset; // header sits flush below the entire stack
  // RW-239: compose banner/header top positions with iOS safe-area so the
  // Dynamic Island / notch doesn't overlap buttons in standalone PWA mode.
  const withSafeTop = (n) => `calc(${n}px + var(--reve-safe-top))`;

  // ─── Staff · HR — separate full-screen workspace (own plum nav), admin-only ───
  // RW-1046/1047: HR is reached from the Sidebar (admins) or the in-module
  // workspace switcher. It replaces the brokerage chrome entirely, so it
  // short-circuits the standard Sidebar + header + <main> shell — same pattern
  // as the Rêve Connect portal above. Placed after the auth/role gates and all
  // hooks (user / activeNav / effectiveIsAdmin / effectiveUser are defined
  // above), so this early return never sits above a hook (Rule 11).
  if (user && activeNav === 'StaffHR') {
    if (effectiveIsAdmin) {
      return (
        <ErrorBoundary>
          <React.Suspense fallback={<AuthRouteLoader label="Loading Staff · HR" />}>
            <StaffApp user={effectiveUser} />
          </React.Suspense>
        </ErrorBoundary>
      );
    }
    // Phase 5: office-staff self-service. A staff_self user gets the RESTRICTED
    // "My Space" surface (own record + time off + documents only — never the admin
    // shell). The server forces self-scope on every read/write regardless of this gate.
    const evRoles = (effectiveUser && (effectiveUser.roles || (effectiveUser.role ? [effectiveUser.role] : []))) || [];
    if (evRoles.includes('staff_self')) {
      return (
        <ErrorBoundary>
          <React.Suspense fallback={<AuthRouteLoader label="Loading My Space" />}>
            <StaffSelfService user={effectiveUser} />
          </React.Suspense>
        </ErrorBoundary>
      );
    }
    return <Navigate to="/" replace />;
  }

  /* Direction C route switch. RW-2313 corrected this note: it claimed the
     helper was "shared by the RW-1075 shell short-circuit (desktop) and the
     standard chrome render (mobile / focused flows) below", i.e. two call
     sites. There is ONE (the `activeNav === 'One Pipeline'` branch further
     down) — the desktop short-circuit no longer routes through here. It matters
     because two call sites would mount these screens twice, and every board
     below now emits MODULE_ENTERED on mount.

     RW-2329 moved Coming Soon / Buyer Needs (and their two records) OUT of
     here into renderMarketScreen — they are Connect › Market modules, not
     Deals surfaces. This helper is now One Pipeline only. */
  const renderDealsScreen = () => (
    location.pathname === '/deals/tasks' ? (
      <DealsMyTasks user={userWithRole} mobile={mobile} canManage={canManageDeals} isAdmin={isAdmin} startPage={userDefaultView} />
    ) : location.pathname === '/deals/home' ? (
      <DealsHome user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/new' ? (
      <DealsNewDeal user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/chat' ? (
      <DealsChatStart user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/payouts' ? (
      <DealsPayouts user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/commissions' ? (
      <DealsCommissions user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/review' ? (
      <DealsBrokerReview user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/people' ? (
      <DealsPeople user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/leadership' ? (
      <DealsLeadership user={userWithRole} mobile={mobile} />
    ) : location.pathname === '/deals/roster' ? (
      <DealsRoster user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : location.pathname === '/deals/docs' ? (
      <DealsCompanyDocs user={userWithRole} mobile={mobile} canManage={canManageDeals} isAdmin={isAdmin} />
    ) : location.pathname === '/deals/settings' ? (
      <DealsSettings user={userWithRole} mobile={mobile} isAdmin={isAdmin} />
    ) : /^\/deals\/[^/]+$/.test(location.pathname) ? (
      // RW-1541: the deal record lives IN the module — every surface's deal
      // click lands here so the rail never disappears. Real role flags for
      // everyone: agents naturally get read-only (canManageDeals false) and
      // the server enforces scope regardless. Unknown ids render DealDetail's
      // own not-found state — honest, unlike the old silent board fallback.
      <DealDetail
        dealId={location.pathname.split('/')[2]}
        mobile={mobile}
        canManage={canManageDeals}
        canApproveGate={canApproveDealGate}
        isAdmin={isAdmin}
        canDeleteDeal={isSuperAdmin && !isAgentView}
        backTo="/deals"
        backLabel="Deals"
      />
    ) : location.pathname === '/deals' ? (
      <DealsPipeline user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : (
      // RW-1541: deeper unknown /deals/* paths 404 instead of silently
      // rendering the board.
      <NotFound />
    )
  );

  /* RW-2329 — Connect › Market. The two boards and their two records used to
     live inside renderDealsScreen, which only runs behind
     `activeNav === 'One Pipeline'`; that is what put the Deals module's chrome
     on top of them. They render here instead, behind their own nav keys (and,
     since RW-3647, under /connect/* URLs — the old /deals/* deep links
     redirect), keeping every service they share with Deals (the listing form, deal-first creation
     through DealsNewDeal, the match engine feeding the pipeline) — this is a
     presentation move, not a decoupling.

     🔴 ONE call site, exactly like renderDealsScreen has ONE (the note above).
     Every board below emits MODULE_ENTERED on mount (RW-2313), so a second
     call site would mount them twice and double-count every visit — the exact
     regression splitting this helper out could reintroduce.
     src/App.marketRoutes.test.js pins the count at one.

     The `/:id` records sit in this same helper, so a record gets its board's
     nav treatment: pathToNav's two-segment rule resolves /connect/coming-soon/x
     to 'Coming Soon' (see NAV_TO_PATH above), and the branch order here puts
     the exact board path ahead of the RE, so the board never swallows a record.

     An unmatched path cannot reach here (activeNav is one of the two keys only
     for paths the two REs match), but the 404 fallback keeps the helper honest
     if a key is ever widened. */
  const renderMarketScreen = () => (
    location.pathname === '/connect/coming-soon' ? (
      <DealsComingSoon user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : COMING_SOON_BOARD_RE.test(location.pathname) ? (
      // RW-2305: /connect/coming-soon/:id is the in-module record — the board
      // pushes it, Back / Escape close it, prev/next replace over the board's
      // filter. Same nav highlight as the board (pathToNav above).
      <ComingSoonDetail user={userWithRole} mobile={mobile} canManage={canManageDeals} isAdmin={isAdmin} />
    ) : location.pathname === '/connect/buyer-needs' ? (
      <DealsBuyerNeeds user={userWithRole} mobile={mobile} canManage={canManageDeals} />
    ) : BUYER_NEEDS_BOARD_RE.test(location.pathname) ? (
      // RW-2307: /connect/buyer-needs/:id is the in-module record — the board
      // pushes it, Back / Escape close it, prev/next replace over the board's
      // set. Same nav highlight as the board (pathToNav above).
      <BuyerNeedDetail user={userWithRole} mobile={mobile} canManage={canManageDeals} isAdmin={isAdmin} />
    ) : (
      <NotFound />
    )
  );

  // RW-1075b (Clint's call): the RW-1075 DealsShell takeover is retired —
  // One Pipeline renders inside the standard workspace like every other
  // module. The MAIN rail owns the Deals icon and the second rail its
  // sections (Sidebar 'deals' group), so global chrome (search, bell,
  // messages, account, bug reporter, impersonation banner) is simply
  // always there — nothing to re-add, no mode switch, no exit button.

  return (
    <ToastProvider>
    <CelebrationProvider user={userWithRole}>
    <FocusProvider user={userWithRole}>
    <DealsRolesProvider value={dealsRoles}>
    <DealCloseHoldProvider>
    <WhatsNewCalloutsProvider value={whatsNewCallouts}>
    {/* RW-2552: the shell paddings are published as CSS variables and padded
        FROM them, so `.reve-main`'s floor (index.css) subtracts exactly what
        the shell pads. The floor itself and box-sizing live on the class: the
        CSSOM drops `100dvh` on a browser that doesn't know it, and only a
        stylesheet can carry the `@supports` fallback. */}
    <div
      className="reve-app-root"
      style={{
        ...styles.appRoot,
        '--reve-shell-top': withSafeTop(headerTopOffset),
        '--reve-shell-bottom': mobile ? 'calc(72px + var(--reve-safe-bottom, 0px))' : '0px',
        // RW-3408: the corner buttons' height above what the shell already pads (the mobile nav)
        '--reve-fab-clearance': user ? `${fabStackReach(mobile) - (mobile ? 72 : 0)}px` : '0px',
        paddingTop: 'var(--reve-shell-top)',
        paddingBottom: 'var(--reve-shell-bottom)',
      }}
    >
      {/* Skip to main content link — visible only on focus for keyboard users */}
      <a href="#main-content" style={{
        position: 'absolute', top: -40, left: 0, background: '#EE8172', color: '#fff',
        padding: '8px 16px', zIndex: 10000, fontSize: 14, fontWeight: 600,
        fontFamily: "'Jost', sans-serif", textDecoration: 'none', borderRadius: '0 0 8px 0',
        transition: 'top 150ms ease',
      }} onFocus={(e) => { e.currentTarget.style.top = '0'; }}
         onBlur={(e) => { e.currentTarget.style.top = '-40px'; }}>
        Skip to main content
      </a>
      {/* RW-187: Impersonation banner — UNMISSABLE. Stays fixed at the top of
          the entire app until the super admin clicks Stop Viewing. */}
      {isImpersonating && impersonatedUser ? (
        <div role="alert" style={{
          position: 'fixed', top: withSafeTop(alertOffset), left: 0, right: 0, height: IMPERSONATION_H,
          background: '#5C50A1', color: '#FFFFFF', zIndex: 9999,
          padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 12, fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)', boxSizing: 'border-box',
        }}>
          <IconAlert size={16} color="#EFDD84" aria-hidden="true" />
          <span>
            Viewing as: <strong>{impersonatedUser.displayName || impersonatedUser.email || 'Unknown'}</strong>
            {impersonatedUser.email ? <> ({impersonatedUser.email})</> : null}
            {' — role: '}
            <strong>{impersonatedUser.role || 'agent'}</strong>
          </span>
          <button type="button" onClick={() => { stopImpersonation(); navigate('/admin'); }}
            style={{
              background: 'transparent', color: '#FFFFFF', border: '1px solid #FFFFFF',
              borderRadius: 4, padding: '4px 12px', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: "'Jost', sans-serif", marginLeft: 4,
            }}>
            Stop Viewing
          </button>
        </div>
      ) : null}
      {showOnboarding && user ? (
        <OnboardingFlow user={userWithRole} onComplete={() => {
          try { localStorage.setItem(`reve_onboarding_done_${userWithRole.uid}`, '1'); } catch {}
          setShowOnboarding(false);
          // RW-697 Phase 3 (Pattern A): drop the new agent straight INTO their
          // onboarding checklist after the profile wizard, instead of /feed.
          // The wizard only shows for fresh non-admin accounts, so anyone here
          // has an auto-provisioned (Phase 2) onboarding instance to land on.
          // Fully dismissible — the sidebar item + banner keep it reachable, so
          // this is a gentle takeover, not a lock. Mark the session as redirected
          // so the smart-redirect can't bounce them back to /feed.
          try { sessionStorage.setItem('reve_hasRedirected', '1'); } catch {}
          if (location.pathname !== '/onboarding') navigate('/onboarding');
        }} />
      ) : null}
      {/* RW-175: PWAShell renders BEFORE the notification banner so the SW
          update banner is visually on top and the notification banner stacks
          directly underneath. */}
      <PWAShell topOffset={withSafeTop(impersonationOffset)} onUpdateBannerVisibilityChange={setSwUpdateBannerVisible} />
      <PushNotificationManager user={userWithRole} topOffset={withSafeTop(swUpdateOffset)} onBannerVisibilityChange={setNotifBannerVisible} />
      {/* ─── Event Alert Banner ─── */}
      <EventAlertBanner
        user={userWithRole}
        onVisibilityChange={setAlertBannerVisible}
        onViewEvent={(event) => {
          if (event?.id) navigate(`/events/${encodeURIComponent(event.id)}`);
        }}
      />
      {/* ─── Offline Banner ─── */}
      {!isOnline && !offlineDismissed ? (
        <div style={{
          position: 'fixed', top: withSafeTop(headerTopOffset), left: !mobile && user ? sidebarWidth : 0, right: 0,
          zIndex: 50, background: '#F59E0B', padding: '8px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600, color: '#fff',
        }}>
          <span>You're offline. Changes will sync when you reconnect.</span>
          <button onClick={() => setOfflineDismissed(true)} style={{
            background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: 4, padding: '2px 8px',
            fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer',
          }}>Dismiss</button>
        </div>
      ) : null}
      {/* ─── Onboarding Progress Banner ─── */}
      {onboardingProgress && !onboardingBannerDismissed && !showOnboarding && user ? (
        <div style={{
          position: 'fixed', top: withSafeTop(headerTopOffset), left: !mobile && user ? sidebarWidth : 0, right: 0,
          zIndex: 49, background: 'linear-gradient(90deg, #EE8172, #5C50A1)', padding: '8px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, fontFamily: "'Jost', sans-serif",
        }}>
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
            You're {onboardingProgress.percent}% through onboarding — keep going!
          </span>
          <button onClick={() => navigate('/onboarding')} style={{
            background: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px',
            fontSize: 12, fontWeight: 700, color: '#A63F33', cursor: 'pointer',
          }}>View Checklist</button>
          <button onClick={() => setOnboardingBannerDismissed(true)} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 16,
            cursor: 'pointer', padding: '0 4px', lineHeight: 1,
          }} aria-label="Dismiss">×</button>
        </div>
      ) : null}
      {/* ─── Slim Top Header (Sidebar is primary nav) ─── */}
      <header style={{
        position: 'fixed',
        top: withSafeTop(headerTopOffset),
        left: !mobile && user ? sidebarWidth : 0,
        right: 0,
        height: 56,
        background: '#fff',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        zIndex: 50,
        transition: 'left 200ms ease',
        fontFamily: "'Jost', sans-serif",
      }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#0F1011', fontFamily: "'Jost', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginLeft: mobile ? 52 : 0 }}>
            {/* RW-1786: rail-destination pages get a Section › Band › Item
               trail (standalone sections collapse to just their name — the
               Home suppression); detail routes keep their richer titles. No
               admin bypass on the navRail2 behavior flag. */}
            {(() => {
              if (mobile || featureFlags?.navRail2?.enabled === false) return getHeaderTitle(location.pathname);
              // RW-3700: a Connect detail page keeps its section's trail.
              const trail = navTrailForPath(location.pathname) || connectDetailTrail(location.pathname);
              if (!trail) return getHeaderTitle(location.pathname);
              return [trail.section, trail.band, trail.item].filter(Boolean).join(' › ');
            })()}
          </h1>
        </div>

        {/* RW-2963: the right cluster may shrink; the avatar stays on-screen. */}
        <div style={HEADER_CLUSTER_STYLE}>
          {/* Search */}
          {/* RW-3839: the 🔍 opens the full search, not a weaker look-alike. */}
          <SearchBar
            onOpen={() => {
              if (location.pathname === '/search') return;
              markNavSource('search'); // RW-1789: topbar search
              navigate('/search');
            }}
          />

          {/* RW-1600: Ask Rêve — global header affordance. An assistant is
              summoned from wherever you are, not a place you go; the rail
              slot moved here (flag-gated exactly like the /ask-reve route). */}
          {isFeatureEnabled('askReve', featureFlags, isAdmin) ? (
            <button
              type="button"
              style={styles.headerIconBtn}
              aria-label="Ask Rêve — AI assistant"
              title="Ask Rêve"
              onClick={() => { setMenuOpen(false); navigate('/ask-reve'); }}
            >
              <IconSparkles size={18} aria-hidden="true" />
            </button>
          ) : null}

          {/* Direct Messages */}
          <div style={styles.avatarWrap}>
            <button
              type="button"
              style={styles.headerIconBtn}
              aria-label={dmUnreadCount > 0 ? `Messages (${dmUnreadCount} unread)` : 'Messages'}
              onClick={() => {
                // If user is already on the full Messages page, the drawer would be a duplicate.
                // Just close the drawer (in case it's open) and no-op the navigation.
                if (location.pathname.startsWith('/messages')) {
                  setChatDrawerOpen(false);
                } else if (mobile) {
                  setActiveNav('Messages');
                } else {
                  setChatDrawerOpen((prev) => !prev);
                }
                setMenuOpen(false);
              }}
            >
              <IconComment size={18} aria-hidden="true" />
              {dmUnreadCount > 0 ? (
                <span aria-hidden="true" style={styles.notifBadge}>{dmUnreadCount > 9 ? '9+' : dmUnreadCount}</span>
              ) : null}
            </button>
          </div>

          {/* Notification Bell */}
          <NotificationBell user={userWithRole} />

          {/* Bug Reporter */}
          <BugReporter user={userWithRole} mobile={mobile} />

          {/* RW-2963: the view pill + avatar menu. On phones the Admin/Agent
              switch moves into the avatar menu so the cluster fits at 320px. */}
          <HeaderAccountMenu
            user={user}
            mobile={mobile}
            canSwitchView={isRealAdmin}
            viewMode={viewMode}
            setViewMode={setViewMode}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            onSettings={() => setActiveNav('Settings')}
            onLogout={handleLogout}
          />
        </div>
      </header>

      {/* Agent view mode banner */}
      {isRealAdmin && isAgentView ? (
        <div style={{ background: '#EE8172', color: '#fff', textAlign: 'center', padding: '4px 12px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span>Viewing as Agent</span>
          <button onClick={() => setViewMode('admin')} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Switch to Admin</button>
        </div>
      ) : null}

      {/* Direct Messages panel removed — now using full-page Messages component */}

      {/* ─── Mobile Nav ─── */}
      {/* RW-2378: same gating inputs as Sidebar — the bottom bar derives its
          contents from navigation.js now, so the two mobile nav surfaces
          cannot drift apart the way they had since RW-1782. */}
      {mobile ? (
        <MobileNav
          user={effectiveUser}
          isAdmin={effectiveIsAdmin}
          isTrainer={effectiveIsTrainer}
          isStaff={effectiveCanViewDeals}
          windingDown={isWindingDown}
        />
      ) : null}

      {/* ─── Page Content + Chat Drawer ─── */}
      {/* RW-2552: main's vertical box — the 56px header padding and the
          viewport floor — lives on `.reve-main` in index.css. appRoot's
          padding already carries the banner stack and MobileNav, and the class
          subtracts both; a `calc(100vh - N)` here stacked on top of them.
          Inline keeps only the horizontal chrome (sidebar / chat margins). */}
      <main id="main-content" role="main" className="reve-main" style={{ marginLeft: !mobile && user ? sidebarWidth : 0, marginRight: (!mobile && chatDrawerOpen && windowWidth >= 1200) ? 380 : 0, transition: 'margin-left 200ms ease, margin-right 250ms ease-in-out' }}>
      <ErrorBoundary fallbackTitle="Page failed to load">
      <React.Suspense fallback={<FeedSkeleton />}>
        {activeNav === 'Dashboard' ? (
          <Dashboard user={effectiveUser} isAdmin={effectiveIsAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Home' || activeNav === 'Feed' ? (
          <FeedLayout user={effectiveUser} isAdmin={effectiveIsAdmin} mobile={mobile} posts={posts}>
            {/* RW-1788: /feed?filter=important — Important Posts is a real,
                deep-linkable filter route (nav row, shares, notifications). */}
            {/* RW-2246: Feed reads ?filter=important itself (Rule 31) — one reader of the key, not two. */}
            <Feed user={effectiveUser} isAdmin={effectiveIsAdmin} totalUsers={totalUsers} posts={posts} mobile={mobile} />
          </FeedLayout>
        ) : null}

        {activeNav === 'Saved Posts' ? (
          <SavedPosts user={effectiveUser} />
        ) : null}

        {activeNav === 'Tech Stack' ? (
          <TechStackPage user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Sign Lab' ? (
          <SignLabPage isSuperAdmin={isSuperAdmin} user={userWithRole} mobile={mobile} />
        ) : null}

        {activeNav === 'People' ? (
          <PeopleDirectory posts={posts} user={userWithRole} mobile={mobile} />
        ) : null}

        {activeNav === 'Recognition' ? (
          <RecognitionModule user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Org Chart' ? (
          <OrgChart user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Offices' ? (
          <OfficeDirectory user={userWithRole} isAdmin={isAdmin} mobile={mobile} onViewAgents={(officeId) => navigate('/people?office=' + officeId)} />
        ) : null}

        {activeNav === 'Groups' ? (
          <Groups user={userWithRole} isAdmin={isAdmin} mobile={mobile} posts={posts} />
        ) : null}

        {activeNav === 'Vendors' ? (
          <VendorDirectory user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Photos' ? (
          <PhotosPage user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {/* RW-1524: legacy Coming Soon + Buyer Needs modules retired — the
            /deals boards own these surfaces (photo/edit flow ported to
            ListingFormModal.js). RW-2299: the redirect KEEPS the record id —
            /coming-soon/:id, ?listing=, /buyer-needs/:id, ?profile= land on
            the /connect/…/:id form; bare paths still land on the boards.
            RW-3647: /deals/coming-soon/… and /deals/buyer-needs/… (the
            pre-move URLs) forward to /connect/… with path, query and hash. */}
        <LegacyDeepLinkRedirect />

        {activeNav === 'Events' ? (
          <EventsApp
            user={userWithRole}
            isAdmin={isAdmin}
            mobile={mobile}
          />
        ) : null}

        {activeNav === 'Leaderboard' ? (
          <Leaderboard user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'My Production' ? (
          <ProductionDashboard user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Documents' ? (
          location.pathname === '/documents/library' ? (
            <ReferenceLibrary user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          ) : (
            <DocumentsPage user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          )
        ) : null}

        {activeNav === 'Training' ? (
          <TrainingCatalog user={userWithRole} isAdmin={isAdmin} isTrainer={isTrainer} mobile={mobile} />
        ) : null}

        {activeNav === 'Tutorials' ? (
          <TutorialsPage user={userWithRole} isAdmin={isAdmin} isTrainer={isTrainer} mobile={mobile} />
        ) : null}

        {/* RW-1642 What's New — machine-written changelog + entry detail */}
        {activeNav === 'WhatsNew' ? (
          /^\/whats-new\/[^/]+$/.test(location.pathname)
            ? <WhatsNewDetail entryId={location.pathname.split('/')[2]} mobile={mobile} isAdmin={isAdmin} />
            : <WhatsNewPage mobile={mobile} isAdmin={isAdmin} />
        ) : null}

        {/* RW-655: in-platform help center — visible to all authenticated users */}
        {activeNav === 'Help' ? (
          <HelpPage user={userWithRole} mobile={mobile} isAdmin={isAdmin} />
        ) : null}

        {/* RW-1024/RW-1026 — new-hire intra-module bar. Shows ONLY on the
            new-hire surface; staff/ops get the AdminOnboardingTab rail instead.
            The "view as agent" toggle flips a staff user back to the new-hire
            surface (isStaffOnboarding=false), which doubles as a preview. */}
        {(location.pathname === '/onboarding' || location.pathname.startsWith('/onboarding/')) && !isStaffOnboarding ? (
          <OnboardingBar
            items={HIRE_NAV}
            activeId={activeHireId(location.pathname)}
            onSelect={(it) => navigate(it.path)}
            // RW-786: phone width — the bar compacts (label collapses to its
            // icon chip) so the scrollable tab strip gets the room.
            mobile={mobile}
          />
        ) : null}

        {/* RW-1026 — unified /onboarding host: one role-aware surface. Staff/ops
            → the onboarding management rail (tracker · templates · finance ·
            invite · provisioning…); new hires → their own checklist. */}
        {activeNav === 'Onboarding' && location.pathname === '/onboarding' ? (
          <React.Suspense fallback={<ObRouteFallback />}>
          {isStaffOnboarding ? (
            <AdminOnboardingTab
              user={userWithRole}
              onPreviewAsHire={() => { setViewMode('agent'); navigate('/onboarding'); }}
            />
          ) : (
            <OnboardingChecklist user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          )}
          </React.Suspense>
        ) : null}

        {/* RW-990: Agent Information Form (onboarding intake task t01) */}
        {location.pathname === '/onboarding/agent-info' ? (
          <React.Suspense fallback={<ObRouteFallback />}>
          <AgentInfoForm
            user={userWithRole}
            // RW-1178 → RW-3902: no instanceId prop. The form resolves the
            // hire's own instance itself with the checklist's rule
            // (useOwnOnboardingInstance → pickOwnOnboardingInstance). The old
            // prop came from the banner listener's limit(1) read, which drops
            // archived/finished docs — so a hire with a running checklist
            // beside an older instance was told theirs "isn't set up yet".
            onSubmitted={() => navigate('/onboarding/status')}
            mobile={mobile}
          />
          </React.Suspense>
        ) : null}

        {/* RW-989: agent self-status — "where am I / when am I live" */}
        {location.pathname === '/onboarding/status' ? (
          <React.Suspense fallback={<ObRouteFallback />}>
          <SelfStatus
            user={userWithRole}
            mobile={mobile}
            // RW-1237: an "On you · right now" row passes its itemId so the
            // checklist deep-links (?item=<id>) to the exact card; without one
            // it stays a plain /onboarding navigation.
            onOpenChecklist={(itemId) => navigate(itemId ? `/onboarding?item=${encodeURIComponent(itemId)}` : '/onboarding')}
            allUsers={allUsers}
            onMessageConcierge={() => openConciergeDM({ me: userWithRole, allUsers, navigate })}
          />
          </React.Suspense>
        ) : null}

        {/* RW-569: onboarding completion / "you're live" screen */}
        {location.pathname === '/onboarding/success' ? (
          <React.Suspense fallback={<ObRouteFallback />}>
          <SuccessScreen
            user={userWithRole}
            mobile={mobile}
            allUsers={allUsers}
            onMessageConcierge={() => openConciergeDM({ me: userWithRole, allUsers, navigate })}
            onContinue={() => navigate('/')}
            // RW-1718: the way back — the completed checklist as a read-only
            // record (?view=checklist is the OnboardingChecklist
            // resolveChecklistView door). SPA navigation, not the component's
            // full-reload fallback.
            onViewChecklist={() => navigate('/onboarding?view=checklist')}
          />
          </React.Suspense>
        ) : null}

        {/* RW-570/RW-1219: Louisiana license transfer task (agent files online in myRECportal; staff verify) */}
        {location.pathname === '/onboarding/lrec' ? (
          <React.Suspense fallback={<ObRouteFallback />}>
          <LrecTransferTask
            user={userWithRole}
            mobile={mobile}
            onBack={() => navigate('/onboarding')}
          />
          </React.Suspense>
        ) : null}

        {/* Adoption Dashboard — accessible to admin OR trainer.
            Trainers do NOT see the full Admin Dashboard, only this read-only
            adoption view. Checked BEFORE the Admin route so /admin/adoption
            doesn't fall through into AdminDashboard. */}
        {location.pathname === '/admin/adoption' && (isAdmin || isTrainer) ? (
          <AdoptionDashboard user={userWithRole} />
        ) : null}

        {/* Darwin Reporting — open to any signed-in user. reportingApi forces
            leadership-vs-self scope server-side: leadership sees firm-wide
            dashboards, agents see only their own production/commissions. */}
        {location.pathname === '/reports' ? (
          <ReportsPage user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {/* RW-2013 My Numbers — agent-facing reporting. Same boundary as
            /reports: reportingApi pins a non-leader to their own uid
            server-side, so this route needs no role gate of its own. The nav
            row is flag-gated (myNumbers, dark until launch); the route stays
            reachable so the flag controls discovery, not access. */}
        {location.pathname === '/my-numbers' ? (
          <MyNumbersPage user={userWithRole} mobile={mobile} />
        ) : null}

        {/* RW-1026 — /admin/onboarding retired (removed, not redirected). The path
            maps to a no-render nav key (see NAV_TO_PATH), so it renders nothing
            rather than falling through to the Admin dashboard. The live
            "Onboarding" surface is /onboarding (role-aware). */}

        {/* RW-826: offboarding queue — relational-core surface, admin-gated.
            The offboardingApi callable additionally allows staff doc-roles.
            RW-1633: non-admins on a stale link get a restricted card (the
            AdminOnboardingTab guard pattern), never a blank pane. */}
        {location.pathname === '/admin/offboarding' ? (
          isAdmin ? (
            <OffboardingPipeline />
          ) : (
            <div style={{ maxWidth: 460, margin: '48px auto', padding: '24px 28px', background: '#fff', border: '1px solid #F3EEE6', borderRadius: 12, textAlign: 'center', fontFamily: "'Jost', sans-serif" }}>
              <div style={{ fontSize: 20, marginBottom: 6 }} aria-hidden="true">🔒</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1F2937' }}>Restricted — admin access required</div>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Ask an administrator if you believe you should have access.</div>
            </div>
          )
        ) : null}

        {location.pathname === '/admin/recruiting' && isAdmin ? (
          <RecruitingPage user={userWithRole} />
        ) : null}

        {/* RW-1012: Agent Roster — admin-only source of record. List at
            /admin/roster, full-page profile at /admin/roster/{uid} (id passed as
            a prop, like DealDetail). agentsApi gates comp server-side. */}
        {location.pathname.startsWith('/admin/roster') && isAdmin ? (
          /^\/admin\/roster\/[^/]+$/.test(location.pathname) ? (
            <AgentProfileDetail agentId={location.pathname.split('/')[3]} mobile={mobile} />
          ) : (
            <AgentRoster user={userWithRole} mobile={mobile} />
          )
        ) : null}

        {/* RW-1524: the legacy deal routes retired with the /admin/deals board
            (RW-844/845) and /my-deals list (RW-846) — /deals owns them all now.
            The route CONTRACTS survive (Rule 26): stored notification/email
            links and bookmarks to /admin/deals/{id} and /my-deals/{id} remap
            to the One Pipeline record, list/new paths to the board/picker. */}
        {location.pathname.startsWith('/admin/deals') || location.pathname.startsWith('/my-deals') ? (
          (() => {
            const seg = location.pathname.split('/').filter(Boolean);
            const id = seg[0] === 'admin' ? seg[2] : seg[1];
            if (id === 'new') return <Navigate to="/deals/new" replace />;
            return <Navigate to={id ? `/deals/${id}` : '/deals'} replace />;
          })()
        ) : null}

        {location.pathname === '/refer-agent' ? (
          <ReferAgent user={effectiveUser} onClose={() => navigate('/dashboard')} />
        ) : null}

        {/* RW-360: Redirect non-admin users away from /admin — no blank page */}
        {activeNav === 'Admin' && !isAdmin && !isTrainer ? <Navigate to="/" replace /> : null}

        {activeNav === 'Admin' && isAdmin && location.pathname !== '/admin/adoption' && location.pathname !== '/admin/onboarding' && location.pathname !== '/admin/offboarding' && location.pathname !== '/admin/recruiting' && !location.pathname.startsWith('/admin/deals') && !location.pathname.startsWith('/admin/roster') ? (
          <AdminDashboard user={userWithRole} posts={posts} mobile={mobile} userRole={userRole} isSuperAdmin={isSuperAdmin} />
        ) : null}

        {activeNav === 'Settings' ? (
          <Settings
            user={effectiveUser}
            isAdmin={isAdmin}
            onEditProfile={() => setShowProfileEditor(true)}
            onSignOut={handleLogout}
          />
        ) : null}

        {activeNav === 'Messages' ? (
          <Messages user={userWithRole} mobile={mobile} />
        ) : null}

        {/* RW-1524: the legacy Transactions module retired — every capability
            lives on the deal record now (M8 docs/sign, M9 money, /deals/review
            compliance). A deal deep-link (/transactions/{txId}) remaps through
            the board; deals keep source_transaction_id for provenance. */}
        {location.pathname.startsWith('/transactions') ? (
          <Navigate to="/deals" replace />
        ) : null}

        {/* RW-494 Rêve Sign. RW-3307: one screen, the DocuSeal requests sent
            for signature; every /sign/* path lands on it. */}
        {activeNav === 'Rêve Sign' ? (
          <SignApp user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Listings' ? (
          location.pathname === '/listings/new' ? (
            <CreateListingWizard user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          ) : location.pathname.match(/^\/listings\/[^/]+$/) && location.pathname !== '/listings' ? (
            <ListingDetail listingId={location.pathname.split('/')[2]} user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          ) : (
            <ListingsPage user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          )
        ) : null}

        {/* RW-1610 Flipbooks. The editor and the flip viewer are height-driven
            surfaces, so they get an explicit box — <main> is min-height only,
            and a percentage height inside it collapses to zero. RW-2561: that
            box is <main>'s own content-box height, published by .reve-main; the
            `calc(100vh - 128px)` it replaced left no room for the safe-area
            inset, so on an iPhone the viewer overflowed by the whole of it. */}
        {activeNav === 'Flipbooks' ? (
          <div style={{ height: 'var(--reve-page-min-height)', overflow: 'auto' }}>
            <FlipbookApp user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          </div>
        ) : null}

        {activeNav === 'Buyers' ? (
          location.pathname.match(/^\/buyers\/[^/]+$/) && location.pathname !== '/buyers' ? (
            <BuyerDetail buyerId={location.pathname.split('/')[2]} user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          ) : (
            <BuyersPage user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
          )
        ) : null}

        {/* CON-1 (RW-1114) — sub-routes (/contacts, /contacts/today, /contacts/
            relationships, /contacts/organize, /contacts/health, /contacts/:id)
            are resolved inside ContactsApp (the SignApp pattern). */}
        {activeNav === 'Contacts' ? (
          <ContactsApp user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {location.pathname === '/notifications' ? (
          <NotificationsPage user={userWithRole} mobile={mobile} />
        ) : null}

        {activeNav === 'Calendar' ? (
          <Calendar user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {/* RW-1524: /pipeline (the early unified prototype) retired — /deals
            is the pipeline. */}
        {location.pathname === '/pipeline' ? (
          <Navigate to="/deals" replace />
        ) : null}

        {activeNav === 'My Tasks' ? (
          <MyTasks user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {/* RW-1291 — sub-routes (/ninja-flow, /today, /ford, /reviews,
            /pipeline, /agenda, /submit, /history, /admin) are resolved inside
            NinjaFlow (the SignApp pattern). */}
        {activeNav === 'Ninja FLOW' ? (
          <NinjaFlow user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {/* AC-1 (RW-1305) — Academy sub-routes (/academy, /library, /coaching,
            /learning, /mentorship, /inbox, /coach, …) are resolved inside
            AcademyApp (the SignApp pattern). */}
        {activeNav === 'Academy' ? (
          <AcademyApp user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {/* RW-2896 Email Signatures (Company › Resources, RW-2917) — sub-pages
            resolve inside SignaturesApp (the AcademyApp pattern); its Admin tab
            shows for isAdmin, which "Viewing as Agent" already turns off. */}
        {activeNav === 'Email Signatures' ? (
          <SignaturesApp user={userWithRole} isAdmin={isAdmin} mobile={mobile} onEditProfile={() => setShowProfileEditor(true)} />
        ) : null}

        {/* RW-814 Ask Rêve — AI chat assistant. Flag `askReve` gates the route
            (see PATH_TO_FLAG); chat + the admin panel resolve inside
            AskReveApp (the AcademyApp pattern). */}
        {activeNav === 'Ask Rêve' ? (
          <AskReveApp user={userWithRole} isAdmin={isAdmin} mobile={mobile} />
        ) : null}

        {activeNav === 'Focus Mode' ? (
          <FocusMode user={userWithRole} mobile={mobile} />
        ) : null}

        {/* RW-1524: /compliance-review → /deals/review — the ONE queue
            (RW-1518 convergence; the ticket's single sanctioned redirect). */}
        {location.pathname === '/compliance-review' ? (
          <Navigate to="/deals/review" replace />
        ) : null}

        {/* RW-1502/RW-1504: Buyer Broker Agreements — open to any signed-in user;
            dealsApi bba* actions scope non-staff callers to their own agreements
            server-side (staff see the whole tenant). */}
        {activeNav === 'Buyer Agreements' ? (
          <BuyerBrokerAgreements user={userWithRole} mobile={mobile} />
        ) : null}

        {/* RW-1077/RW-1078: Direction C unified pipeline (flag `onePipeline`,
            dark). Open to any signed-in user — dealsApi `list` force-scopes
            non-staff to their own deals server-side; drag/manage gated by
            canManageDeals here AND canManage server-side. */}
        {/* RW-1075b: ALL One Pipeline surfaces render here, inside the
            standard workspace — the second rail (Sidebar 'deals' group) is
            the module nav; DealsNav pills cover mobile. */}
        {activeNav === 'One Pipeline' ? renderDealsScreen() : null}

        {/* RW-2329: Connect › Market — Coming Soon and Buyer Needs are their
            own modules, rendered OUTSIDE renderDealsScreen so they no longer
            inherit the One Pipeline branch (and, with it, Deals chrome). Their
            URLs are /connect/* since RW-3647. ONE call site — see renderMarketScreen
            for why a second would double-fire MODULE_ENTERED. */}
        {activeNav === 'Coming Soon' || activeNav === 'Buyer Needs' ? renderMarketScreen() : null}

        {/* RW-1524: /pipeline-dashboard retired — the PG leadership rollup at
            /deals/leadership (RW-1522) replaced its Firestore reads. */}
        {location.pathname === '/pipeline-dashboard' ? (
          <Navigate to="/deals/leadership" replace />
        ) : null}

        {/* RW-1568: search indexes nav destinations too, gated by the same
            values the rail gets so it can never offer a page the user
            cannot open. */}
        {activeNav === 'search' ? (
          <SearchPage user={userWithRole} mobile={mobile}
            isAdmin={effectiveIsAdmin} isTrainer={effectiveIsTrainer} isStaff={effectiveCanViewDeals} />
        ) : null}

        {/* RW-991 Connect Extensions */}
        {activeNav === 'Connect Search' ? (
          <SearchConnect user={userWithRole} />
        ) : null}
        {activeNav === 'Town Halls' ? (
          <TownHalls user={userWithRole} isAdmin={isAdmin} />
        ) : null}

        {/* RW-899 stage 1 — wind-down landing for offboarding agents. The route
            gate above sends every non-allowed path to '/', and the smart login
            redirect is suppressed while winding down, so '/' renders this. */}
        {isWindingDown && location.pathname === '/' ? (
          <WindDownScreen
            onOpenDeals={() => navigate('/deals')}
          />
        ) : null}

        {/* RW-672: Catch-all 404 — when pathToNav() returned the '_none'
            sentinel, no other conditional render above matched. Skip '/'
            because the smart-redirect effect navigates away on first render. */}
        {activeNav === '_none' && location.pathname !== '/'
          // RW-2299: a legacy /coming-soon or /buyer-needs URL is redirecting
          // above — don't paint the 404 for the one frame before it lands.
          && !legacyDeepLinkTarget(location.pathname, location.search) ? (
          <NotFound />
        ) : null}
      </React.Suspense>
      </ErrorBoundary>
      </main>

      {/* ─── Chat Drawer (desktop push / narrow overlay) ─── */}
      {!mobile && chatDrawerOpen ? (
        <React.Suspense fallback={null}>
          <div style={{
            position: 'fixed', top: 72, right: 0, bottom: 0, width: 380,
            background: '#fff', borderLeft: '1px solid #E8E9EB',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.08)', zIndex: 45,
            transform: 'translateX(0)', transition: 'transform 250ms ease-in-out',
            display: 'flex', flexDirection: 'column',
          }}>
            <Messages user={userWithRole} mobile={false} isDrawer onClose={() => setChatDrawerOpen(false)} />
          </div>
        </React.Suspense>
      ) : null}

      {/* ─── Left Sidebar Navigation ─── */}
      {user ? <Sidebar user={effectiveUser} isAdmin={effectiveIsAdmin} isTrainer={effectiveIsTrainer} isStaff={effectiveCanViewDeals} windingDown={isWindingDown} mobile={mobile} badges={{ overdue: overdueCount, tasksDue: tasksDueCount, unreadMessages: dmUnreadCount || 0, requiredTraining: requiredTrainingCount, whatsNewUnread: whatsNewCallouts.unreadCount, unreadImportant: unreadImportantCount }} highlights={whatsNewCallouts.highlightKeys} onboardingProgress={onboardingProgress} onboardingComplete={onboardingComplete} onboardingNewCount={onboardingNewCount} /> : null}

      {/* ─── Quick Actions FAB ─── */}
      <QuickActionsFab user={userWithRole} isAdmin={isAdmin} mobile={mobile} />

      {/* Feature-flag redirect toast */}
      {appToast ? (
        <div style={{ position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)', zIndex: 2000, padding: '10px 20px', background: '#1F2937', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {appToast}
        </div>
      ) : null}

      {/* ─── Profile Editor Modal (from Settings) ─── */}
      {showProfileEditor ? (
        <React.Suspense fallback={null}>
          <ProfileEditor
            user={userWithRole}
            targetUser={user}
            isAdmin={isAdmin}
            onClose={() => setShowProfileEditor(false)}
            // RW-2917: the signature is built from these details, so My
            // Profile links to it wherever the module is on.
            onOpenSignature={isFeatureEnabled('emailSignatures', featureFlags, isAdmin)
              ? () => { setShowProfileEditor(false); navigate('/email-signatures'); }
              : undefined}
          />
        </React.Suspense>
      ) : null}
      {/* RW-3295: "your part is ready to sign" — upper right, on every screen, until it's signed anywhere */}
      {user ? (
        <React.Suspense fallback={null}>
          <AgentTurnAlert navigate={navigate} isAdmin={isAdmin} mobile={mobile} />
        </React.Suspense>
      ) : null}
      {/* ─── Focus Mode floating widget (persists across all routes) ─── */}
      <React.Suspense fallback={null}>
        <FocusWidget />
      </React.Suspense>
      {/* RW-1642 What's New spotlight — one featured entry, first visit to the
          changed screen, already marked seen server-side (never returns). */}
      {whatsNewCallouts.activeSpotlight ? (
        <React.Suspense fallback={null}>
          <WhatsNewSpotlight
            spotlight={whatsNewCallouts.activeSpotlight}
            onDismiss={whatsNewCallouts.dismissSpotlight}
            mobile={mobile}
          />
        </React.Suspense>
      ) : null}
    </div>
    </WhatsNewCalloutsProvider>
    </DealCloseHoldProvider>
    </DealsRolesProvider>
    </FocusProvider>
    </CelebrationProvider>
    </ToastProvider>
  );
}

export default App;
